// TODO(slice-7 / post-FK-breadcrumb): TableExplorerTab is >2000 lines.
// Once the breadcrumb work stabilises, split this file into the
// connection/data layer, the row-details/inline-panel handlers, and the
// FK-drilldown callbacks (EP3/EP5/EP6) — each as its own module. Out of
// scope for the Slice 3 review fixes.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FileEdit, Frame, Hexagon } from 'lucide-react';
import { useSplitterDrag } from '../../../../layout/hooks/useSplitterDrag';
import { TableExplorerToolbar } from './TableExplorerToolbar';
import { TableExplorerGrid } from './TableExplorerGrid';
import { openDataProfileTab } from '../../data-profile/services/dataProfileNavigation';
import { openFindUsagesTab } from '../../dependency-viewer/services/findUsagesNavigation';
import { isFindUsagesSupportedEngine } from '../../dependency-viewer/services/findUsagesEngine';
import { TableExplorerStatusBar } from './TableExplorerStatusBar';
import { TableExplorerPagination } from './TableExplorerPagination';
import { WindowedModeBanner } from './WindowedModeBanner';
import { TableSelector } from './TableSelector';
import { FilterWizard } from '../../common/components/filters/FilterWizard';
import { SortWizard } from './SortWizard';
import { QueryPreviewDialog } from './QueryPreviewDialog';
import { QuickEditDialog } from './QuickEditDialog';
import { SaveChangesDialog } from './SaveChangesDialog';
import { ExitEditModeDialog } from './ExitEditModeDialog';
import { DismissBreadcrumbConfirmDialog } from './DismissBreadcrumbConfirmDialog';
import { useWorkspaceStore } from '../../core/store/workspaceStore';
import { useConnectionStore } from '../../../connections/store/connectionStore';
import { useSessionStore } from '../../../sessions/store/sessionStore';
import { useAnalytics } from '../../../../core/analytics';
import { markHasRunQuery } from '../../first-connect-bridge';
import { getSelectedTableInfo } from '../../../database-objects/utils/getSelectedTableInfo';
import type { ExplorerSelectedTable } from './TableSelector';
import { DisconnectedConnectionBanner } from '../../../sessions/components/DisconnectedConnectionBanner';
import { ConnectionTabFailureBanner } from '../../../connection-diagnostics/components/ConnectionTabFailureBanner';
import { useTableData } from '../hooks/useTableData';
import { useBlueprintTableData } from '../hooks/useBlueprintTableData';
import { applyBlueprintTableEdits, readSidecar } from '../../../database-blueprint/services/databaseBlueprintApi';
import {
  applyPendingChangesToRows,
  mapSidecarRowsToObjects,
  rowObjectsToPositional,
} from '../utils/blueprintSidecarRows';
import type { DatabaseBlueprintSidecar } from '../../../database-blueprint/types/databaseBlueprintTypes';
import { lookupForeignKey, useTableMetadata } from '../hooks/useTableMetadata';
import { useInboundForeignKeys } from '../hooks/useInboundForeignKeys';
import { usePendingChanges } from '../hooks/usePendingChanges';
import { useTableExplorerMcpCommands } from '../hooks/useTableExplorerMcpCommands';
import { applyTableExplorerChanges, fetchTableExplorerCellValue, previewTableExplorerSelect } from '../services/tableExplorerApi';
import { cancelQueryV2 } from '../../../database-objects/services/queryExecutor';
import type { WorkspaceTabComponentProps, FilterGroup, FilterCondition, SpatialFilterContext, SortDefinition, PendingChange, PendingInsert, PendingUpdate, TableExplorerColumn } from '../../core/types/workspaceTypes';
import { isTableExplorerTab } from '../../core/types/workspaceTypes';
import { createExecutionId } from '../../../../types/queryExecution';
import { engineSupports, isValidEngineId, type DatabaseEngineId } from '../../../../core/engines';
import type { TableInfo } from '../hooks/useAvailableTables';
import { normalizeColumnName } from '../../common/utils/normalizeColumnName';
import { findColumnByName } from '../../common/utils/columnNameMatch';
import { nextCancelRequest } from '../../common/utils/cancelEscalation';
import { getSupportsQueryPreview } from '../utils/queryPreviewUtils';
import { RowDetailsPanel } from './RowDetailsPanel';
import type { TableExplorerInboundForeignKey } from '../../../../types/tableExplorer';
import type { ResultsExportFormat } from '../../../../types/resultsExport';
import { exportResults } from '../../common/services/resultsExportApi';
import { toast } from '../../../../components/ui/toaster';
import { requestPinToDashboard } from '../../dashboards/services/pinToDashboard';
import { showExportSuccessToast } from '../../common/utils/exportSuccessToast';
import { buildTableExplorerExportResultSet } from '../utils/buildExportResultSet';
import { computeEffectiveIdentityColumnsSet, resolveTableExplorerIdentityOptions } from '../utils/tableExplorerIdentity';
import { tableDataToCsv, tableDataToMarkdown, tableDataToHtml, resultSetToInsert, writeRichToClipboard } from '../../common/utils/clipboardFormatters';
import { writeClipboardText } from '../../../../lib/clipboard';
import type { InsertIdentityMode } from '../../common/utils/insertStatementBuilder';
import { useAppStore } from '../../../../store/appStore';
import { categorizeSpatialColumn, sniffSpatialBlobColumns, type SpatialColumnInput } from '../../common/utils/spatialColumnCategorization';
import { loadSpatialDecoder } from '../../spatial/decode/loadSpatialDecoder';
import { computeColumnProfiles, type ColumnProfileStats } from '../../common/utils/columnProfiling';
import { pickCellFilterOperator } from '../../common/utils/cellFilterOperator';
import type { CopyFormat } from './TableExplorerToolbar';
import { ChartBuilderDialog } from '../../charts/components/ChartBuilderDialog';
import { ChartRendererHost } from '../../charts/components/ChartRendererLazy';
import { useChartData } from '../../charts/hooks/useChartData';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../../components/ui/dialog';
import type { ChartConfig } from '../../charts/types/chartTypes';
import type { QueryColumn, QueryResultSet } from '../../../../types/queryExecution';
import { SpatialResultsMapPanel } from '../../spatial/results-map/SpatialResultsMapPanel';
import { useSpatialDrawFilter } from './useSpatialDrawFilter';
import { getSpatialResultColumns } from '../../spatial/results-map/spatialResultColumns';
import type { SpatialMapViewport } from '../../spatial/map/SpatialMapLazy';
import { SpatialIndexHint } from '../../spatial/index-coach/SpatialIndexHint';
import { hasSpatialIndex } from '../../spatial/index-coach/spatialIndexAdvisor';
import { useProFeature } from '../../../../core/licensing/hooks/useProFeature';
import { checkExportLimit } from '../../common/utils/personalModeLimits';
import { PersonalModeExportLimitMessage } from '../../common/components/PersonalModeExportLimitMessage';
import { UpgradeDialog } from '../../../../core/licensing/components/UpgradeDialog';
import { SingleRowView, useSingleRowData } from '../../single-row';
import type { SingleRowNodeRef } from '../../single-row';
import { CrossTableSingleRowView } from './CrossTableSingleRowView';
import { FkBreadcrumb } from './FkBreadcrumb/FkBreadcrumb';
import { useTabNavigationTree } from './FkBreadcrumb/useTabNavigationTree';
import { computeTableExplorerTabTitle } from './FkBreadcrumb/breadcrumbTabTitle';
import { computeEffectiveExplorerContext } from './FkBreadcrumb/effectiveExplorerContext';
import {
  setNodeEditMode as patchNodeEditMode,
  setNodePendingChanges as patchNodePendingChanges,
  subtreeHasPendingChanges,
} from './FkBreadcrumb/navTreeEditState';
import { useInvalidateMetaInfo, useMetaInfo } from '../../common/hooks/useMetaInfo';
import { useLookupCacheWriteThrough } from '../../common/hooks/useLookupCacheWriteThrough';
import { useConnectionPrewarm } from '../../common/hooks/useConnectionPrewarm';
import {
  pickJoinTableHint,
  probeJoinCollapse,
} from '../../single-row/utils/joinTableProbeRunner';
import type { JoinTableHint } from '../../single-row/utils/joinTableHintTypes';
// Slice 3 review fixes: `ForeignKeyPopover` now owns the first-run-hint
// trigger + reads `fkOpenDefault` directly from `useAppStore`, so this file
// no longer needs those imports.
import { buildInboundForeignKeyFilters } from '../utils/inboundForeignKeyUtils';
import { buildRowNodePk } from '../utils/buildRowNodePk';
import { checkAndQueueConnectionLostRetry } from '../utils/connectionLostGuard';
import { derivePaginationModel } from '../utils/paginationModel';
import {
  freshColumnsFromMetadataRefetch,
  reconcilePinnedColumns,
} from '../utils/reconcilePinnedColumns';
import { filterSignature } from '../../common/stores/jsonStructureFilteredAggregatorStore';
import { registerPinCapture } from '../../common/services/pinCaptureRegistry';
import { registerGridExport, type GridExportOutcome } from '../../common/store/gridExportRegistry';
import { selectTableExplorerDisplaySource } from '../utils/selectTableExplorerDisplay';
import { useBetaFeature } from '@/core/beta';

const EMPTY_PENDING_CHANGES: PendingChange[] = [];
// Stable empty references — the display memo below returns these so a phase with
// nothing to show doesn't churn the grid's identity-keyed memos every render.
const EMPTY_DISPLAY_ROWS: Record<string, unknown>[] = [];
const EMPTY_DISPLAY_COLUMNS: TableExplorerColumn[] = [];
const EMPTY_INBOUND_FOREIGN_KEYS: TableExplorerInboundForeignKey[] = [];

/** Real defaults for the three CH-13 splitters below (row-details side/bottom, map pane). */
const ROW_DETAILS_BOTTOM_DEFAULT_HEIGHT = 260;
const ROW_DETAILS_SIDE_DEFAULT_WIDTH = 384;
const MAP_PANE_DEFAULT_WIDTH = 440;

/** Keyboard resize steps (Shift = 4x), shared by all three px-based splitters below. */
const TABLE_EXPLORER_SPLIT_KEY_STEP = 8;
const TABLE_EXPLORER_SPLIT_KEY_STEP_BIG = 32;

/**
 * Pull the backend's approximate whole-table total off a fetch result.
 *
 * `TableExplorerFetchRowsResult.estimatedRowCount` is set only when the exact
 * `COUNT(*)` missed its settle budget on an unfiltered fetch (see
 * `electron/table-explorer/queryBuilder/rowCountEstimate.ts`). It is read
 * structurally because this component consumes TWO interchangeable data hooks
 * (`useTableData` for live tables, `useBlueprintTableData` for the sidecar) and
 * only the live one can ever carry it; a blueprint page — filtered and paged in
 * userspace — always has an exact total. When it is absent the tab behaves
 * exactly as before: unknown total, has-more pagination.
 */
function readEstimatedRowCount(result: unknown): number | undefined {
  const value = (result as { estimatedRowCount?: unknown } | null | undefined)?.estimatedRowCount;
  return typeof value === 'number' ? value : undefined;
}

export function TableExplorerTab({ tabId, className = '' }: WorkspaceTabComponentProps) {
  // All hooks must be called before any conditional returns

  // Workspace store
  const updateTabContent = useWorkspaceStore((state) => state.updateTabContent);
  const currentTab = useWorkspaceStore(
    useCallback((state) => state.tabs.find((tab) => tab.id === tabId), [tabId])
  );

  // Connection store
  const activeConnections = useConnectionStore((state) => state.activeConnections);
  const savedConnections = useConnectionStore((state) => state.connections);
  const connectToDatabase = useConnectionStore((state) => state.connectToDatabase);
  const markConnectionDisconnected = useConnectionStore((state) => state.markConnectionDisconnected);

  // Session store
  const currentSession = useSessionStore((state) => state.currentSession);
  const updateConnectionStatus = useSessionStore((state) => state.updateConnectionStatus);

  // Analytics
  const { track } = useAnalytics();

  // Track whether we've already sent the analytics event for this table
  const hasTrackedRef = useRef<string | null>(null);

  // Banner dismissal state
  const [bannerDismissed, setBannerDismissed] = useState(false);

  const [zoomLevel, setZoomLevel] = useState(1);

  // Row selection + row details (bottom or side)
  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null);
  const [rowDetailsRowIndex, setRowDetailsRowIndex] = useState<number | null>(null);
  const [rowDetailsLayout, setRowDetailsLayout] = useState<'bottom' | 'side'>('bottom');

  const rowDetailsSplitRef = useRef<HTMLDivElement>(null);
  const rowDetailsDividerHeight = 6; // h-1.5
  const rowDetailsMinHeight = 140;
  const gridMinHeight = 140;
  const [rowDetailsHeightPx, setRowDetailsHeightPx] = useState(ROW_DETAILS_BOTTOM_DEFAULT_HEIGHT);
  const rowDetailsSideDividerWidth = 8; // w-2
  const rowDetailsSideMinWidth = 240;
  const rowDetailsSideMaxWidth = 800;
  const gridMinWidth = 240;
  const [rowDetailsSideWidthPx, setRowDetailsSideWidthPx] = useState(ROW_DETAILS_SIDE_DEFAULT_WIDTH);

  // Spatial map pane (T14) — a right-edge grid|map split. Mirrors the row-details
  // "side" divider precedent (vertical col-resize, width-based) with its own state.
  const [isMapPaneOpen, setIsMapPaneOpen] = useState(false);
  const mapPaneDividerWidth = 8; // w-2
  const mapPaneMinWidth = 280;
  const mapPaneMaxWidth = 900;
  const [mapPaneWidthPx, setMapPaneWidthPx] = useState(MAP_PANE_DEFAULT_WIDTH);
  // Local (non-persisted) map viewport + decoded-window SRIDs — T15 consumes
  // these for the "Filter by map view" bbox chip and the distance-chip point.
  const [spatialViewport, setSpatialViewport] = useState<SpatialMapViewport | null>(null);
  const [spatialColumnSrids, setSpatialColumnSrids] = useState<number[]>([]);

  // Query preview dialog state
  const [isQueryPreviewOpen, setIsQueryPreviewOpen] = useState(false);
  const [previewQuery, setPreviewQuery] = useState('');

  // Export state
  const [isExporting, setIsExporting] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  // Column profiling toggle (local state — mirrors ResultsGrid pattern)
  const [showColumnProfiling, setShowColumnProfiling] = useState(false);

  // Chart state
  const [isChartBuilderOpen, setIsChartBuilderOpen] = useState(false);
  const [isChartViewOpen, setIsChartViewOpen] = useState(false);
  const [chartConfig, setChartConfig] = useState<ChartConfig | undefined>(undefined);

  // Export limit state
  const [showExportLimitDialog, setShowExportLimitDialog] = useState(false);
  const [exportLimitRowCount, setExportLimitRowCount] = useState(0);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const { canUse: canExportFull } = useProFeature('export-full');

  // Edit mode dialog states
  const [isSaveChangesOpen, setIsSaveChangesOpen] = useState(false);
  const [isExitEditModeOpen, setIsExitEditModeOpen] = useState(false);
  const [exitAfterSave, setExitAfterSave] = useState(false);
  const [isQuickEditOpen, setIsQuickEditOpen] = useState(false);
  const [quickEditData, setQuickEditData] = useState<{
    primaryKey: Record<string, unknown>;
    field: string;
    oldValue: unknown;
    newValue: unknown;
  } | null>(null);
  const quickEditCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Refs for last attempted save args — used to replay the same operation on retry.
  // Mirrors the `lastExecutionArgsRef` pattern from useQueryExecution (Slice 5).
  const lastSaveChangesArgsRef = useRef<PendingChange[]>([]);
  const lastQuickEditArgsRef = useRef<PendingUpdate | null>(null);
  // Stable ref to handlePreviewQuery — avoids circular useCallback dependency.
  const handlePreviewQueryRef = useRef<() => Promise<void>>(async () => {});
  // Stable refs to replay save-edit and quick-edit — avoids circular useCallback dependency.
  const handleExecuteSaveRef = useRef<(changes: PendingChange[]) => Promise<{ success: boolean; error?: string }>>(async () => ({ success: false }));
  const handleExecuteQuickEditRef = useRef<(change: PendingUpdate) => Promise<{ success: boolean; error?: string }>>(async () => ({ success: false }));

  // Memoized values that depend on currentTab
  const isValidTab = !!(currentTab && isTableExplorerTab(currentTab));
  const tabContent = isValidTab ? currentTab.content : null;
  const connectionInfo = isValidTab ? currentTab.connectionInfo : undefined;
  const connectionId = connectionInfo?.connectionId;

  // Open a connection ahead of the tab's first fetch (remote pools hold no
  // standing connection by design). Opening/activating the tab IS the intent,
  // so it fires on mount and whenever the tab retargets; the hook throttles.
  // No UI surface, so no analytics event.
  const notePrewarmIntent = useConnectionPrewarm(connectionId, tabContent?.database);
  useEffect(() => {
    notePrewarmIntent();
  }, [notePrewarmIntent]);

  // Blueprint-edit mode: when set, the grid renders from the blueprint's
  // `.data.json` sidecar (NOT a live-DB query) and Save writes back to the
  // sidecar + regenerates `.data.sql`. The linked DB's connectionId/database
  // still drive MetaInfo (JSON/enum/loose-FK overlays); they're never queried
  // for rows here. See `openBlueprintTableExplorerTab`.
  const blueprintEdit = tabContent?.blueprintEdit;
  const isBlueprintEdit = !!blueprintEdit;
  // Bumped after a successful blueprint Save so the sidecar grid re-reads from
  // the freshly-written `.data.json` (instead of a live-DB refetch).
  const [blueprintReloadToken, setBlueprintReloadToken] = useState(0);
  // `pendingChanges` / `effectiveIsEditMode` are wired to the per-node owner
  // (when a navigationTree is active) further down — see `activeEditOwnerId`.
  // The tab-level fallback below applies only to legacy (pre-tree) tabs.

  // Get the engine type from the active connection
  const activeConnection = useMemo(
    () => activeConnections.find((conn) => conn.id === connectionId),
    [activeConnections, connectionId]
  );
  const connectionEngine = activeConnection?.connection?.engine;
  // Carry the connection's engine through AS-IS (incl. 'oracle') so behaviour
  // and analytics see the real engine — matches openTableExplorerTab.ts /
  // MetaInfoManagerDialog. The old allowlist omitted 'oracle' and fell back to
  // 'mssql', which mislabeled every Oracle Table Explorer analytics event.
  // 'mssql' remains only as the behavioural default for a genuinely
  // absent/invalid engine (engineId must be a concrete DatabaseEngineId).
  const engineId: DatabaseEngineId = useMemo(() => {
    return connectionEngine && isValidEngineId(connectionEngine) ? connectionEngine : 'mssql';
  }, [connectionEngine]);

  const defaultSchema = useMemo(() => {
    if (engineId === 'pgsql') return 'public';
    if (engineId === 'sqlite' || engineId === 'mysql') return '';
    return 'dbo';
  }, [engineId]);

  const supportsQueryPreview = useMemo(
    () => getSupportsQueryPreview(activeConnection?.connection),
    [activeConnection?.connection]
  );
  const supportsInboundForeignKeyExplorer = useMemo(
    () => engineSupports(engineId, 'tableExplorer', 'supportsInboundForeignKeyExplorer'),
    [engineId]
  );

  // Effective workspace context (Concerns 4 + 6 of the FK breadcrumb follow-up):
  // when a non-root TableNode is active in the breadcrumb tree, the grid must
  // render for THAT node's table — not the original `tabContent.tableName` the
  // tab was first opened on. The active node also carries any inbound-FK
  // locked filters (rendered as immutable chips) plus user-added filters and
  // sorting that survive deactivation/reactivation.
  const navigationTreeForContext = tabContent?.navigationTree ?? null;
  const effectiveContext = useMemo(
    () =>
      computeEffectiveExplorerContext(
        {
          schema: tabContent?.schema ?? defaultSchema,
          tableName: tabContent?.tableName ?? '',
          filters: tabContent?.filters ?? [],
          sorting: tabContent?.sorting ?? [],
        },
        navigationTreeForContext,
      ),
    [
      tabContent?.schema,
      tabContent?.tableName,
      tabContent?.filters,
      tabContent?.sorting,
      navigationTreeForContext,
      defaultSchema,
    ],
  );
  const isTableContext = effectiveContext.kind === 'table';
  const effectiveSchema = isTableContext
    ? effectiveContext.schema
    : tabContent?.schema ?? defaultSchema;
  const effectiveTable = isTableContext
    ? effectiveContext.table
    : tabContent?.tableName ?? '';
  const effectiveLockedFilters = useMemo<ReadonlyArray<FilterGroup>>(
    () => (isTableContext ? effectiveContext.lockedFilters : []),
    [isTableContext, effectiveContext],
  );
  const effectiveUserFilters = useMemo<ReadonlyArray<FilterGroup>>(
    () => (isTableContext ? effectiveContext.userFilters : (tabContent?.filters ?? [])),
    [isTableContext, effectiveContext, tabContent?.filters],
  );
  const effectiveFilters = useMemo<FilterGroup[]>(
    () => [...effectiveLockedFilters, ...effectiveUserFilters],
    [effectiveLockedFilters, effectiveUserFilters],
  );
  const activeFilterSignature = useMemo(
    () => (effectiveFilters.length > 0 ? filterSignature(effectiveFilters) : null),
    [effectiveFilters],
  );
  const effectiveSorting = useMemo<ReadonlyArray<SortDefinition>>(
    () => (isTableContext ? effectiveContext.sorting : tabContent?.sorting ?? []),
    [isTableContext, effectiveContext, tabContent?.sorting],
  );
  const isOnRoot =
    !navigationTreeForContext ||
    navigationTreeForContext.rootId === navigationTreeForContext.activeNodeId;
  const isOnNonRootTable = isTableContext && !isOnRoot;

  // Pin capture (F5 / Task 19; corrected under Codex gate-3 F3) — expose this
  // tab's LIVE Table Explorer definition to the pin button + tab-strip pin
  // menu. Captured from the EFFECTIVE (post-FK-navigation) view values, with
  // locked (inbound-FK) + user filters already flattened into one lossless
  // array by `effectiveFilters` (F3, same `FilterGroup` shape either side).
  // Returns `null` in row/record context — row views aren't capturable (G7) —
  // AND in blueprint-edit mode: a blueprint tab's view has no live-DB
  // connection backing it, so it must never be mintable as a pin from ANY
  // surface (the toolbar already blocks it via `disabledReason`, but the tab
  // context menu drove eligibility purely from `useWorkspacePinState`, which
  // didn't know about blueprint/row-context — see codex-gate3-fixes-brief.md
  // F3). Every effective value the callback reads is a dependency below so a
  // re-registered capture never returns a stale payload. Proven end-to-end by
  // the open-from-pin e2e (Task 24); the pure capture logic + this wiring are
  // unit-covered in `TableExplorerTab.pinCapture.test.ts`.
  useEffect(
    () =>
      registerPinCapture(tabId, () => {
        const database = tabContent?.database;
        if (!isTableContext || isBlueprintEdit || database === undefined) return null;
        return {
          workspaceType: 'table-explorer',
          payload: {
            tableName: effectiveTable,
            schema: effectiveSchema,
            database,
            limit: tabContent?.limit ?? 100,
            selectedColumns: tabContent?.selectedColumns ?? '*',
            filters: effectiveFilters,
            sorting: effectiveSorting as SortDefinition[],
          },
        };
      }),
    [
      tabId,
      isTableContext,
      isBlueprintEdit,
      tabContent?.database,
      tabContent?.limit,
      tabContent?.selectedColumns,
      effectiveTable,
      effectiveSchema,
      effectiveFilters,
      effectiveSorting,
    ],
  );

  // The TableNode that currently owns edit-state. When the breadcrumb is
  // active on a non-root TableNode, edit-mode + pendingChanges live on THAT
  // node so the user can edit child tables independently of the root. When
  // the active node is the root TableNode we also prefer the node's state
  // (so navigating away and back preserves edits). RowNodes are not
  // editable; for them the edit-state owner is `null` and the tab-level
  // fallback applies (Single-Row view doesn't use these fields).
  const activeEditOwnerId = useMemo(() => {
    if (!isTableContext) return null;
    if (!navigationTreeForContext) return null;
    const node = navigationTreeForContext.nodes[navigationTreeForContext.activeNodeId];
    return node && node.kind === 'table' ? node.id : null;
  }, [isTableContext, navigationTreeForContext]);
  const activeEditOwnerNode = useMemo(() => {
    if (!activeEditOwnerId || !navigationTreeForContext) return null;
    const n = navigationTreeForContext.nodes[activeEditOwnerId];
    return n && n.kind === 'table' ? n : null;
  }, [activeEditOwnerId, navigationTreeForContext]);
  const ownerIsEditMode = activeEditOwnerNode?.isEditMode ?? false;
  const ownerPendingChanges = useMemo<ReadonlyArray<PendingChange>>(
    () => activeEditOwnerNode?.pendingChanges ?? EMPTY_PENDING_CHANGES,
    [activeEditOwnerNode],
  );

  // Effective edit state — prefer the active node's owner when a tree is
  // present (per-node edit mode); fall back to the tab-level state for
  // pre-tree tabs (no FK breadcrumb yet).
  const effectiveIsEditMode = activeEditOwnerId
    ? ownerIsEditMode
    : tabContent?.isEditMode ?? false;
  const pendingChanges: PendingChange[] = useMemo(() => {
    const src = activeEditOwnerId
      ? ownerPendingChanges
      : tabContent?.pendingChanges ?? EMPTY_PENDING_CHANGES;
    // Hooks downstream want a mutable PendingChange[]; clone defensively.
    return src === EMPTY_PENDING_CHANGES ? EMPTY_PENDING_CHANGES : Array.from(src);
  }, [activeEditOwnerId, ownerPendingChanges, tabContent?.pendingChanges]);

  // Get the currently selected table from explorer for quick-access in TableSelector
  const explorerSelectedTable = useMemo((): ExplorerSelectedTable | undefined => {
    const selectedInfo = getSelectedTableInfo();
    if (!selectedInfo) return undefined;
    return {
      name: selectedInfo.name,
      schema: selectedInfo.schema,
      database: selectedInfo.database,
    };
  }, []);

  const queryConnectionInfo = useMemo(() => {
    if (!connectionInfo && !connectionId) {
      return undefined;
    }

    return {
      ...connectionInfo,
      connectionId: connectionInfo?.connectionId || connectionId,
      database: tabContent?.database || connectionInfo?.database,
    };
  }, [connectionId, connectionInfo, tabContent?.database]);

  // Check if connection is disconnected
  const sessionConnectionStatus = useMemo(() => {
    if (!connectionId) return undefined;
    return currentSession?.connections.find((c) => c.connectionId === connectionId)?.status;
  }, [connectionId, currentSession]);

  const isInActiveConnections = useMemo(
    () => (connectionId ? activeConnections.some((c) => c.id === connectionId) : false),
    [connectionId, activeConnections]
  );

  const isDisconnected = useMemo(() => {
    // Blueprint-edit mode never queries the live DB — a disconnected (or
    // removed) linked connection is fine. Treat it as "connected" so the
    // disconnected banner stays hidden and editing isn't blocked. MetaInfo
    // overlays simply won't populate when the link is down.
    if (isBlueprintEdit) return false;
    if (!connectionId) return false;
    return (
      sessionConnectionStatus === 'disconnected' ||
      activeConnection?.status === 'disconnected' ||
      (!isInActiveConnections && !!connectionInfo)
    );
  }, [isBlueprintEdit, connectionId, sessionConnectionStatus, activeConnection, isInActiveConnections, connectionInfo]);

  // Reset banner when tab changes or connection changes
  useEffect(() => {
    setBannerDismissed(false);
  }, [tabId, connectionId]);

  // Reset page to 1 when the effective table changes (root retable, drill into
  // a non-root TableNode, or active node flip via breadcrumb).
  useEffect(() => {
    updateTabContent(tabId, { currentPage: 1 });
  }, [tabContent?.database, effectiveSchema, effectiveTable, tabId, updateTabContent]);

  // Reset row details when context changes (table or breadcrumb active node).
  useEffect(() => {
    setSelectedRowIndex(null);
    setRowDetailsRowIndex(null);
    setRowDetailsLayout('bottom');
  }, [tabId, connectionId, tabContent?.database, effectiveSchema, effectiveTable, effectiveIsEditMode]);

  // Derive the active row details from current data (so it updates after refetch)
  const rowDetails = useMemo(() => {
    if (rowDetailsRowIndex === null || !tabContent?.rows) return null;
    const row = tabContent.rows[rowDetailsRowIndex];
    if (!row) return null;
    return { rowIndex: rowDetailsRowIndex, row };
  }, [rowDetailsRowIndex, tabContent?.rows]);

  // Three CH-13 splitters below (row-details bottom/side, map pane): pointer-
  // capture + rAF drag, keyboard step, double-click reset, via the shared
  // `useSplitterDrag` hook. All three keep their pre-existing POSITION-based
  // math unchanged (a panel's size is the distance from the pointer's
  // absolute position to the container's far edge, not a delta from drag
  // start) — only the event plumbing moved from `document` mousemove/mouseup
  // to pointer capture, by reconstructing the absolute clientX/clientY from
  // the hook's delta (`dragStart... + deltaPx`), mirroring the sidebar's
  // Explorer/Scripts divider precedent. Each panel is right/bottom-anchored,
  // so its size is `containerSize - relativePos - dividerSize/2`: dragging
  // AWAY from the anchor grows the panel, dragging TOWARD it shrinks —
  // dragging is therefore the mirror image of the keyboard step, where
  // ArrowRight/ArrowDown always means "increase" (the standard separator
  // convention: the key increases the represented value, i.e. the panel's
  // own width/height — not "moves in that screen direction").
  const rowDetailsBottomDragStartClientYRef = useRef(0);
  const rowDetailsSideDragStartClientXRef = useRef(0);
  const mapPaneDragStartClientXRef = useRef(0);

  const rowDetailsHeightForClientY = useCallback(
    (clientY: number): number => {
      const container = rowDetailsSplitRef.current;
      if (!container) return rowDetailsHeightPx;
      const containerRect = container.getBoundingClientRect();
      const containerHeight = containerRect.height;
      const relativeY = clientY - containerRect.top;
      const maxPanelHeight = Math.max(
        rowDetailsMinHeight,
        containerHeight - gridMinHeight - rowDetailsDividerHeight
      );
      const newPanelHeight = containerHeight - relativeY - rowDetailsDividerHeight / 2;
      return Math.round(Math.max(rowDetailsMinHeight, Math.min(maxPanelHeight, newPanelHeight)));
    },
    [gridMinHeight, rowDetailsDividerHeight, rowDetailsMinHeight, rowDetailsHeightPx]
  );

  const rowDetailsSideWidthForClientX = useCallback(
    (clientX: number): number => {
      const container = rowDetailsSplitRef.current;
      if (!container) return rowDetailsSideWidthPx;
      const containerRect = container.getBoundingClientRect();
      const containerWidth = containerRect.width;
      const relativeX = clientX - containerRect.left;
      const maxPanelWidth = Math.max(
        rowDetailsSideMinWidth,
        Math.min(rowDetailsSideMaxWidth, containerWidth - gridMinWidth - rowDetailsSideDividerWidth)
      );
      const newPanelWidth = containerWidth - relativeX - rowDetailsSideDividerWidth / 2;
      return Math.round(Math.max(rowDetailsSideMinWidth, Math.min(maxPanelWidth, newPanelWidth)));
    },
    [gridMinWidth, rowDetailsSideDividerWidth, rowDetailsSideMaxWidth, rowDetailsSideMinWidth, rowDetailsSideWidthPx]
  );

  const mapPaneWidthForClientX = useCallback(
    (clientX: number): number => {
      const container = rowDetailsSplitRef.current;
      if (!container) return mapPaneWidthPx;
      const containerRect = container.getBoundingClientRect();
      const maxPanelWidth = Math.max(
        mapPaneMinWidth,
        Math.min(mapPaneMaxWidth, containerRect.width - gridMinWidth - mapPaneDividerWidth)
      );
      const newPanelWidth = containerRect.right - clientX - mapPaneDividerWidth / 2;
      return Math.round(Math.max(mapPaneMinWidth, Math.min(maxPanelWidth, newPanelWidth)));
    },
    [gridMinWidth, mapPaneDividerWidth, mapPaneMaxWidth, mapPaneMinWidth, mapPaneWidthPx]
  );

  const rowDetailsBottomSplitter = useSplitterDrag({
    axis: 'y',
    onDrag: (deltaPx) => {
      setRowDetailsHeightPx(rowDetailsHeightForClientY(rowDetailsBottomDragStartClientYRef.current + deltaPx));
    },
    onReset: () => {
      setRowDetailsHeightPx(ROW_DETAILS_BOTTOM_DEFAULT_HEIGHT);
      track('feature:splitter_reset', { surface: 'table-explorer-row-details-bottom' });
    },
    onKeyStep: (direction, big) => {
      const step = big ? TABLE_EXPLORER_SPLIT_KEY_STEP_BIG : TABLE_EXPLORER_SPLIT_KEY_STEP;
      const container = rowDetailsSplitRef.current;
      const maxPanelHeight = container
        ? Math.max(rowDetailsMinHeight, container.getBoundingClientRect().height - gridMinHeight - rowDetailsDividerHeight)
        : rowDetailsHeightPx;
      setRowDetailsHeightPx(Math.max(rowDetailsMinHeight, Math.min(maxPanelHeight, rowDetailsHeightPx + direction * step)));
    },
  });

  const rowDetailsSideSplitter = useSplitterDrag({
    axis: 'x',
    onDrag: (deltaPx) => {
      setRowDetailsSideWidthPx(rowDetailsSideWidthForClientX(rowDetailsSideDragStartClientXRef.current + deltaPx));
    },
    onReset: () => {
      setRowDetailsSideWidthPx(ROW_DETAILS_SIDE_DEFAULT_WIDTH);
      track('feature:splitter_reset', { surface: 'table-explorer-row-details-side' });
    },
    onKeyStep: (direction, big) => {
      const step = big ? TABLE_EXPLORER_SPLIT_KEY_STEP_BIG : TABLE_EXPLORER_SPLIT_KEY_STEP;
      const container = rowDetailsSplitRef.current;
      const maxPanelWidth = container
        ? Math.max(rowDetailsSideMinWidth, Math.min(rowDetailsSideMaxWidth, container.getBoundingClientRect().width - gridMinWidth - rowDetailsSideDividerWidth))
        : rowDetailsSideWidthPx;
      setRowDetailsSideWidthPx(Math.max(rowDetailsSideMinWidth, Math.min(maxPanelWidth, rowDetailsSideWidthPx + direction * step)));
    },
  });

  // Map-pane resize (T14) — the map pane is the RIGHTMOST split, so its width is
  // computed from the container's right edge (same math as the side divider).
  const mapPaneSplitter = useSplitterDrag({
    axis: 'x',
    onDrag: (deltaPx) => {
      setMapPaneWidthPx(mapPaneWidthForClientX(mapPaneDragStartClientXRef.current + deltaPx));
    },
    onReset: () => {
      setMapPaneWidthPx(MAP_PANE_DEFAULT_WIDTH);
      track('feature:splitter_reset', { surface: 'table-explorer-map-pane' });
    },
    onKeyStep: (direction, big) => {
      const step = big ? TABLE_EXPLORER_SPLIT_KEY_STEP_BIG : TABLE_EXPLORER_SPLIT_KEY_STEP;
      const container = rowDetailsSplitRef.current;
      const maxPanelWidth = container
        ? Math.max(mapPaneMinWidth, Math.min(mapPaneMaxWidth, container.getBoundingClientRect().width - gridMinWidth - mapPaneDividerWidth))
        : mapPaneWidthPx;
      setMapPaneWidthPx(Math.max(mapPaneMinWidth, Math.min(maxPanelWidth, mapPaneWidthPx + direction * step)));
    },
  });

  const handleRowDetailsPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      rowDetailsBottomDragStartClientYRef.current = e.clientY;
      rowDetailsBottomSplitter.onPointerDown(e);
    },
    [rowDetailsBottomSplitter]
  );

  const handleRowDetailsSidePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      rowDetailsSideDragStartClientXRef.current = e.clientX;
      rowDetailsSideSplitter.onPointerDown(e);
    },
    [rowDetailsSideSplitter]
  );

  const handleMapPanePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      mapPaneDragStartClientXRef.current = e.clientX;
      mapPaneSplitter.onPointerDown(e);
    },
    [mapPaneSplitter]
  );

  useEffect(() => {
    if (!rowDetails || rowDetailsLayout !== 'bottom' || effectiveIsEditMode) return;
    const container = rowDetailsSplitRef.current;
    if (!container) return;
    const containerHeight = container.getBoundingClientRect().height;
    const maxPanelHeight = Math.max(
      rowDetailsMinHeight,
      containerHeight - gridMinHeight - rowDetailsDividerHeight
    );
    setRowDetailsHeightPx((prev) => (prev > maxPanelHeight ? Math.round(maxPanelHeight) : prev));
  }, [
    rowDetails,
    rowDetailsLayout,
    gridMinHeight,
    rowDetailsDividerHeight,
    rowDetailsMinHeight,
    effectiveIsEditMode,
  ]);

  useEffect(() => {
    if (!rowDetails || rowDetailsLayout !== 'side' || effectiveIsEditMode) return;
    const container = rowDetailsSplitRef.current;
    if (!container) return;
    const containerWidth = container.getBoundingClientRect().width;
    const maxPanelWidth = Math.max(
      rowDetailsSideMinWidth,
      Math.min(rowDetailsSideMaxWidth, containerWidth - gridMinWidth - rowDetailsSideDividerWidth)
    );
    setRowDetailsSideWidthPx((prev) => (prev > maxPanelWidth ? Math.round(maxPanelWidth) : prev));
  }, [
    rowDetails,
    rowDetailsLayout,
    gridMinWidth,
    rowDetailsSideDividerWidth,
    rowDetailsSideMaxWidth,
    rowDetailsSideMinWidth,
    effectiveIsEditMode,
  ]);

  // Handle reconnection attempt
  const handleReconnect = useCallback(async (): Promise<boolean> => {
    if (!connectionId) return false;

    const connection = savedConnections.find((c) => c.id === connectionId);
    if (!connection) return false;

    const success = await connectToDatabase(connection);
    if (success) {
      updateConnectionStatus(connectionId, 'connected');
      setBannerDismissed(false);
    } else {
      markConnectionDisconnected(connectionId);
      updateConnectionStatus(connectionId, 'disconnected');
    }
    return success;
  }, [connectionId, savedConnections, connectToDatabase, updateConnectionStatus, markConnectionDisconnected]);

  // Column profiling stats — only computed when toggle on
  const columnProfiles = useMemo<Map<string, ColumnProfileStats>>(() => {
    if (!tabContent || !showColumnProfiling || tabContent.columns.length === 0 || tabContent.rows.length === 0) {
      return new Map();
    }
    const queryColumns: QueryColumn[] = tabContent.columns.map((col) => ({
      id: col.name,
      name: col.name,
      dataType: col.dataType,
    }));
    const profiles = computeColumnProfiles(queryColumns, tabContent.rows);
    return new Map(profiles.map((p) => [p.columnId, p]));
  }, [showColumnProfiling, tabContent]);

  const handleToggleColumnProfiling = useCallback(() => {
    setShowColumnProfiling((prev) => !prev);
  }, []);

  // T5 — WKT copy/export parity: native-spatial column NAMES (not ids — the
  // export result set built below has no `.id` field, so `spatialColumnIds`
  // sent to the export/copy pipelines must be names). Mirrors
  // TableExplorerGrid's own `nativeSpatialColumnIds` computation exactly,
  // gated the same way (spatial capability + SQLite BLOB sniff). Uses its own
  // `useMetaInfo` call (same query key as `metaInfoForJoinHints` declared
  // further down this file — TanStack Query dedupes, so this is not an extra
  // fetch) since this memo needs to sit above `handleExport`/`handleCopy`.
  const { data: spatialMetaInfo } = useMetaInfo(connectionId ?? null, tabContent?.database ?? null);
  const nativeSpatialColumnNames = useMemo(() => {
    const names = new Set<string>();
    if (!tabContent) return names;
    if (!engineSupports(engineId, 'spatial', 'supportsSpatialTypes')) return names;
    for (const column of tabContent.columns) {
      const input: SpatialColumnInput = {
        engine: engineId,
        schema: effectiveSchema ?? '',
        table: effectiveTable,
        name: column.name,
        dataType: column.dataType,
      };
      if (categorizeSpatialColumn(input, spatialMetaInfo) === 'native-spatial') {
        names.add(column.name);
      }
    }
    if (engineId === 'sqlite') {
      const blobInputs: SpatialColumnInput[] = tabContent.columns.map((column) => ({
        engine: engineId,
        schema: effectiveSchema ?? '',
        table: effectiveTable,
        name: column.name,
        dataType: column.dataType,
      }));
      const promoted = sniffSpatialBlobColumns(blobInputs, tabContent.rows.slice(0, 20));
      for (const column of tabContent.columns) {
        if (promoted.has(column.name)) names.add(column.name);
      }
    }
    return names;
  }, [tabContent, engineId, effectiveSchema, effectiveTable, spatialMetaInfo]);

  // Fetch table metadata FIRST for FK detection, PK columns, identity columns, and full column list
  // This needs to be before useTableData so we can ensure PK columns are always fetched
  // allColumns from metadata is the stable list for column selector/filter/sort wizards
  //
  // FIX-1 (CP-2) — relocated above `handleExport`/`handleCopy` (was declared
  // further down, right before `metadataDataTypeByNormalizedName`) — same
  // "needs to sit above handleExport/handleCopy" reasoning already used for
  // `spatialMetaInfo`. `metadataColumns`/`foreignKeyMap`/etc. are still read
  // further down the file; moving the declaration earlier doesn't change
  // that, only where it happens relative to these handlers.
  const {
    foreignKeyMap,
    primaryKeyColumns: pkColumnsSet,
    identityColumns: identityColumnsSet,
    allColumns: metadataColumns,
    refetch: refetchMetadata,
  } = useTableMetadata({
    connectionId: connectionId || '',
    database: tabContent?.database || '',
    schema: effectiveSchema,
    tableName: effectiveTable,
    // In blueprint-edit mode metadata still helps (FK/loose-FK overlays,
    // length-aware type strings) when the linked DB is connected, but the
    // sidecar is authoritative for PK/identity/columns, so we gate it on
    // having a usable connection and never let its absence block editing.
    enabled: isValidTab && !!connectionId && !!effectiveTable && !isDisconnected,
    engineId,
  });

  // D3 — INSERT copy/export identity awareness (Msg 544 parity with the QE
  // results grid).
  //
  // D1 (codex pre-PR review) — resolved at ACTION time and awaited, exactly
  // like `useExportCopy.ts`'s `resolveIdentityOptions` on the QE grid. The
  // previous memo read `tabContent.columns[].isIdentity`, a snapshot stamped
  // by the column-enrichment effect from the `useTableMetadata` query — a
  // *different* query from the one that puts rows on screen and enables these
  // buttons, and the slower of the two. See `tableExplorerIdentity.ts` for
  // the full failure mode (including the silent `success:false` case).
  const skipIdentity = useAppStore((state) => state.insertSkipIdentity);
  const resolveInsertIdentityOptions = useCallback(
    () =>
      resolveTableExplorerIdentityOptions({
        skipIdentity,
        engineId,
        isBlueprintEdit,
        connectionId,
        database: tabContent?.database,
        schema: effectiveSchema,
        table: effectiveTable,
        columns: tabContent?.columns ?? [],
      }),
    [skipIdentity, engineId, isBlueprintEdit, connectionId, tabContent?.database, tabContent?.columns, effectiveSchema, effectiveTable],
  );

  const handleExport = useCallback(
    // Returns the outcome as well as toasting it — the toolbar ignores it,
    // `ui_export_results` reports it back to the agent. Same contract as the
    // Query Editor grid's `useExportCopy.handleExport` (grid parity).
    async (format: ResultsExportFormat): Promise<GridExportOutcome> => {
      if (!tabContent) return { success: false, error: 'This tab has no loaded table data' };

      const resultSet = buildTableExplorerExportResultSet({
        rows: tabContent.rows,
        columns: tabContent.columns,
        selectedColumns: tabContent.selectedColumns,
        totalRowCount: tabContent.totalRowCount,
        // D3 — lets the SQL (INSERT) export resolve + quote the target table
        // without TE having to duplicate insertStatementBuilder's quoting.
        schema: effectiveSchema,
        table: effectiveTable,
      });

      if (resultSet.rows.length === 0) {
        toast.warning({ title: 'Nothing to export', description: 'No rows are loaded for the current view.' });
        return { success: false, error: 'No rows are loaded for the current view' };
      }

      // Check export limit in Personal mode
      const rowCount = resultSet.rows.length;
      if (!canExportFull) {
        const limitCheck = checkExportLimit(rowCount);
        if (limitCheck.exceedsLimit) {
          setExportLimitRowCount(rowCount);
          setShowExportLimitDialog(true);
          return {
            success: false,
            error: `${limitCheck.description} The app is showing the user the upgrade dialog.`,
          };
        }
      }

      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const fullName = effectiveSchema ? `${effectiveSchema}.${effectiveTable}` : effectiveTable;
      const extension = format === 'markdown' ? 'md' : format;
      const suggestedFileName = `${fullName}-${timestamp}.${extension}`;

      // D1 — resolve identity columns for the SQL (INSERT) export here, at
      // action time, and await it: the answer must not depend on whether a
      // background metadata query has landed yet. `unreliable` means the check
      // itself failed, so warn instead of silently emitting an unwrapped
      // INSERT (Msg 544).
      let identityOptions: { identityColumns?: string[]; identityMode?: InsertIdentityMode } = {};
      if (format === 'sql') {
        const resolved = await resolveInsertIdentityOptions();
        identityOptions = resolved.options;
        if (resolved.unreliable) {
          toast.warning({
            title: 'Could not check identity columns',
            description: 'The INSERT script was generated without identity handling.',
          });
        }
      }

      setIsExporting(true);
      try {
        const result = await exportResults({
          format,
          resultSets: [resultSet],
          suggestedFileName,
          title: `Export ${fullName}`,
          engineId,
          // T5 — WKT export parity (CSV/XLSX only; other formats ignore this).
          // Also fixes a pre-existing gap: `engineId` itself was never sent
          // here, so dialect-aware SQL export was silently unavailable for
          // Table Explorer's own export menu.
          spatialColumnIds: nativeSpatialColumnNames.size > 0 ? [...nativeSpatialColumnNames] : undefined,
          // D3 — identity awareness applies to the SQL (INSERT) export only.
          ...identityOptions,
        });

        if (result.success) {
          showExportSuccessToast(result.filePath, 'table-explorer');
          return { success: true, filePath: result.filePath, rowCount };
        }

        if (!result.cancelled) {
          toast.error({ title: 'Export failed', description: result.error });
        }
        return { success: false, error: result.error, cancelled: result.cancelled };
      } finally {
        setIsExporting(false);
      }
    },
    [tabContent, canExportFull, effectiveSchema, effectiveTable, engineId, nativeSpatialColumnNames, resolveInsertIdentityOptions]
  );

  /**
   * Expose THIS grid's export to `ui_export_results`, exactly as the Query
   * Editor grid does (`ResultsGrid.tsx`) — the shared registry is what keeps
   * the two grids from drifting apart on the agent path. No `exportAllCsv`:
   * Table Explorer paginates and has no streaming export, so the tool tells
   * the agent that rather than quietly exporting the current page as if it
   * were the whole table.
   */
  useEffect(
    () =>
      registerGridExport(tabId, {
        exportLoaded: ({ format }) => handleExport(format),
        loadedRowCount: () => tabContent?.rows.length ?? 0,
      }),
    [tabId, handleExport, tabContent?.rows.length],
  );

  // Copy to clipboard handler
  const handleCopy = useCallback(async (format: CopyFormat) => {
    if (!tabContent || tabContent.rows.length === 0) return;

    // D3 — grid parity with the QE results grid's INSERT copy row cap.
    if (format === 'insert' && tabContent.rows.length > 10000) {
      toast.warning({ title: 'Large result set', description: 'INSERT copy is limited to 10,000 rows. Use Export SQL for larger sets.' });
      return;
    }

    // D1 — same awaited action-time resolution as the SQL export above, for
    // the INSERT copy path.
    let identityOptions: { identityColumns?: string[]; identityMode?: InsertIdentityMode } = {};
    if (format === 'insert') {
      const resolved = await resolveInsertIdentityOptions();
      identityOptions = resolved.options;
      if (resolved.unreliable) {
        toast.warning({
          title: 'Could not check identity columns',
          description: 'The INSERT script was generated without identity handling.',
        });
      }
    }

    try {
      // T5 — WKT copy parity: decode native-spatial columns to WKT before any
      // of the clipboard formatters read them — the lowest shared point every
      // format (csv/markdown/html/insert) reads from.
      let copyRows = tabContent.rows;
      if (nativeSpatialColumnNames.size > 0) {
        const decoder = await loadSpatialDecoder();
        const dataTypeOf = (name: string): string | undefined =>
          tabContent.columns.find((c) => c.name === name)?.dataType;
        copyRows = tabContent.rows.map((row) => {
          let changed = false;
          const next: Record<string, unknown> = { ...row };
          for (const name of nativeSpatialColumnNames) {
            if (!(name in row)) continue;
            const transformed = decoder.transformSpatialForExport(row[name], engineId, dataTypeOf(name));
            if (transformed !== row[name]) {
              next[name] = transformed;
              changed = true;
            }
          }
          return changed ? next : row;
        });
      }

      if (format === 'html') {
        const html = tableDataToHtml(copyRows, tabContent.columns, tabContent.selectedColumns);
        const text = tableDataToCsv(copyRows, tabContent.columns, tabContent.selectedColumns);
        await writeRichToClipboard(text, html);
      } else if (format === 'insert') {
        const resultSet = buildTableExplorerExportResultSet({
          rows: copyRows,
          columns: tabContent.columns,
          selectedColumns: tabContent.selectedColumns,
          totalRowCount: tabContent.totalRowCount,
          schema: effectiveSchema,
          table: effectiveTable,
        });
        const content = resultSetToInsert(resultSet, { engineId, ...identityOptions });
        if (!(await writeClipboardText(content))) return;
      } else {
        const content = format === 'markdown'
          ? tableDataToMarkdown(copyRows, tabContent.columns, tabContent.selectedColumns)
          : tableDataToCsv(copyRows, tabContent.columns, tabContent.selectedColumns);
        if (!(await writeClipboardText(content))) return;
      }
      // D3-2 (review fix) — same event the QE results grid fires for every
      // copy format (`useExportCopy.ts`), fired uniformly here (not just for
      // INSERT) so cross-grid adoption can be queried together. `surface`
      // distinguishes the two grids since QE's event doesn't set one
      // (defaults to query editor there).
      track('feature:results_copy', {
        format,
        surface: 'table-explorer',
        ...(format === 'insert' ? { skip_identity: skipIdentity } : {}),
      });
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch {
      // Clipboard API not available
    }
  }, [tabContent, engineId, nativeSpatialColumnNames, effectiveSchema, effectiveTable, resolveInsertIdentityOptions, skipIdentity, track]);

  const handleToggleRowSidePanel = useCallback(() => {
    if (effectiveIsEditMode) return;
    if (!tabContent?.rows.length) return;

    setRowDetailsRowIndex((current) => {
      // Toggle close when already open in side layout.
      if (rowDetailsLayout === 'side' && current !== null) return null;

      // Open (or keep) for current row details row, selected row, or first row.
      if (current !== null) return current;
      const fallbackIndex = selectedRowIndex ?? 0;
      return tabContent.rows[fallbackIndex] ? fallbackIndex : null;
    });

    setRowDetailsLayout('side');

    setSelectedRowIndex((currentSelected) => {
      if (currentSelected !== null) return currentSelected;
      if (rowDetailsRowIndex !== null) return rowDetailsRowIndex;
      return tabContent.rows.length ? 0 : null;
    });
  }, [rowDetailsLayout, rowDetailsRowIndex, selectedRowIndex, effectiveIsEditMode, tabContent?.rows]);

  // Chart handlers
  const handleOpenChart = useCallback(() => {
    if (chartConfig) {
      // Already have a config, go straight to chart view
      setIsChartViewOpen(true);
    } else {
      // Open builder to create chart
      setIsChartBuilderOpen(true);
    }
  }, [chartConfig]);

  const handleChartConfigured = useCallback((config: ChartConfig) => {
    setChartConfig(config);
    setIsChartBuilderOpen(false);
    setIsChartViewOpen(true);
  }, []);

  // Convert table explorer columns to query columns for chart
  const chartColumns = useMemo((): QueryColumn[] => {
    if (!tabContent?.columns) return [];
    return tabContent.columns.map((col) => ({
      name: col.name,
      dataType: col.dataType,
      isNullable: col.nullable,
    }));
  }, [tabContent?.columns]);

  // Create a result set-like object for the chart
  const chartResultSet = useMemo(() => {
    if (!tabContent?.rows || !chartColumns.length) return undefined;
    return {
      columns: chartColumns,
      rows: tabContent.rows,
      hasMore: false,
    };
  }, [tabContent?.rows, chartColumns]);

  // Use chart data hook
  const { chartData, hasData: hasChartData, resolvedConfig: resolvedChartConfig } = useChartData(
    chartResultSet,
    chartConfig
  );

  const metadataDataTypeByNormalizedName = useMemo(() => {
    const map = new Map<string, string>();
    for (const col of metadataColumns) {
      map.set(normalizeColumnName(col.name), col.dataType);
    }
    return map;
  }, [metadataColumns]);

  // Native-enum signal by column name. Postgres' metadata columns query
  // resolves dataType via format_type (the enum's own type name), so the
  // string can't reveal native-enum-ness — `isNativeEnumType` (sourced from
  // pg_type.typtype='e') is the authoritative signal. Merged into the grid's
  // columns the same way `dataType` is, below.
  const metadataIsNativeEnumByNormalizedName = useMemo(() => {
    const map = new Map<string, boolean>();
    for (const col of metadataColumns) {
      map.set(normalizeColumnName(col.name), col.isNativeEnumType === true);
    }
    return map;
  }, [metadataColumns]);

  // Nullability by column name — needed for the boolean cell-edit picker so
  // it can decide whether to offer a NULL option. Query result sets don't
  // carry nullability per column; only the metadata fetch does.
  const metadataNullableByNormalizedName = useMemo(() => {
    const map = new Map<string, boolean>();
    for (const col of metadataColumns) {
      map.set(normalizeColumnName(col.name), col.nullable);
    }
    return map;
  }, [metadataColumns]);

  // PK columns from live metadata. In blueprint-edit mode the sidecar is the
  // authoritative source (the linked DB may be disconnected) — the real
  // `primaryKeyColumns` is computed after the data hooks (see below) so it can
  // fall back to the sidecar-derived PK. This metadata-only value feeds the
  // (blueprint-disabled) `useTableData` call's `primaryKeyColumns` arg.
  const metadataPrimaryKeyColumns = useMemo(() => Array.from(pkColumnsSet), [pkColumnsSet]);

  // Effective column list for the filter/sort/column-selector wizards. Normally
  // the stable live-metadata list; in blueprint-edit mode (where the linked DB
  // may be disconnected and metadata is empty) we fall back to the grid's
  // sidecar-derived columns so the wizards still have something to pick from.
  const effectiveMetadataColumns = useMemo(
    () => (metadataColumns.length > 0 ? metadataColumns : (tabContent?.columns ?? [])),
    [metadataColumns, tabContent?.columns],
  );

  // Open-from-pin column reconciliation (G8). A pinned Table Explorer view can
  // carry filters/sorts/selected columns captured against an earlier schema.
  // While `pendingColumnReconcile` is set the data query is held disabled
  // (see `useTableData` above) so it never emits SQL with stale column
  // references. Once live column metadata arrives, prune the vanished
  // references, clear the flag (re-enabling the data query), and surface an
  // honest count of what was removed. Inert for ordinary tabs — the flag is
  // only ever set by the open-from-pin service and is never serialized.
  //
  // K2 (fresh fetch): before reconciling, force ONE fresh metadata read so the
  // reconcile pass never trusts a possibly-stale (up-to-5-min) cache entry for
  // the pinned table's columns. L2: the reconcile effect below consumes the
  // refetch's OWN result — `reconcileFreshColumns` holds the fresh column names
  // on success, `'error'` when the refetch failed (TanStack keeps stale cache
  // in the hook's data on error, so the hook's `metadataColumns` must never be
  // used here), and `null` while the forced read hasn't settled.
  const [reconcileFreshColumns, setReconcileFreshColumns] = useState<string[] | 'error' | null>(
    null,
  );
  const reconcileRefetchStartedRef = useRef(false);
  // Round token: a refetch that settles AFTER its gate round was reset
  // (disconnect mid-flight, tab invalidated) must not leak its outcome into a
  // later round — only the current round's settlement may set the state.
  const reconcileRoundRef = useRef(0);
  useEffect(() => {
    if (!isValidTab || !tabContent?.pendingColumnReconcile || isDisconnected) {
      // Gate cleared / not a pin tab / disconnected → reset so a future gate
      // (e.g. re-open after reconnect) forces its own fresh read.
      reconcileRoundRef.current += 1;
      reconcileRefetchStartedRef.current = false;
      setReconcileFreshColumns((prev) => (prev === null ? prev : null));
      return;
    }
    if (reconcileRefetchStartedRef.current) return;
    reconcileRefetchStartedRef.current = true;
    const round = reconcileRoundRef.current;
    refetchMetadata().then(
      (result) => {
        if (reconcileRoundRef.current !== round) return;
        setReconcileFreshColumns(freshColumnsFromMetadataRefetch(result) ?? 'error');
      },
      () => {
        if (reconcileRoundRef.current !== round) return;
        setReconcileFreshColumns('error');
      },
    );
  }, [isValidTab, tabContent?.pendingColumnReconcile, isDisconnected, refetchMetadata]);

  useEffect(() => {
    if (!isValidTab || !tabContent?.pendingColumnReconcile) return;
    // Wait for the forced fresh metadata read (above) to settle — never
    // reconcile against the hook's (possibly stale) cache entry (L2).
    if (reconcileFreshColumns === null) return;

    if (reconcileFreshColumns === 'error' || reconcileFreshColumns.length === 0) {
      // K2 (terminal state): the forced read failed, or settled with no
      // columns (dropped/renamed table). NEVER hold the data query forever:
      // clear the gate, tell the user we're showing the pin as saved, and let
      // the normal data-query path surface its own (honest) error UX.
      // NOTE: the terminal-state + forced-refetch paths are component-level and
      // are covered by the open-from-pin e2e (Task 24), not a unit test — this
      // 2000+-line component has no unit harness. The refetch-result plumbing
      // itself is unit-tested via `freshColumnsFromMetadataRefetch`.
      updateTabContent(tabId, { pendingColumnReconcile: undefined });
      toast.error({
        title: 'Pinned view opened',
        description: 'Could not verify columns for this pinned view — showing it as saved',
      });
      return;
    }

    const reconciled = reconcilePinnedColumns(
      {
        filters: tabContent.filters,
        sorting: tabContent.sorting,
        selectedColumns: tabContent.selectedColumns,
      },
      reconcileFreshColumns,
    );

    updateTabContent(tabId, {
      filters: reconciled.filters,
      sorting: reconciled.sorting,
      selectedColumns: reconciled.selectedColumns,
      pendingColumnReconcile: undefined,
    });

    if (reconciled.droppedCount > 0) {
      const n = reconciled.droppedCount;
      toast.warning({
        title: 'Pinned view opened',
        description: `${n} saved setting${n === 1 ? '' : 's'} referenced missing columns and ${n === 1 ? 'was' : 'were'} removed`,
      });
    }
  }, [
    isValidTab,
    tabContent?.pendingColumnReconcile,
    tabContent?.filters,
    tabContent?.sorting,
    tabContent?.selectedColumns,
    reconcileFreshColumns,
    tabId,
    updateTabContent,
  ]);

  // Fetch inbound (referenced-by) FK definitions for row exploration
  const inboundForeignKeysQuery = useInboundForeignKeys({
    connectionId: connectionId || '',
    database: tabContent?.database || '',
    schema: effectiveSchema,
    tableName: effectiveTable,
    enabled:
      isValidTab &&
      !!connectionId &&
      !!effectiveTable &&
      !isDisconnected &&
      supportsInboundForeignKeyExplorer,
  });
  const inboundForeignKeysData = inboundForeignKeysQuery.data;
  const inboundForeignKeys = inboundForeignKeysData || EMPTY_INBOUND_FOREIGN_KEYS;
  const inboundReferencedColumns = useMemo(() => {
    const columns = new Set<string>();
    for (const fk of inboundForeignKeys) {
      for (const referencedColumn of fk.referencedColumns) {
        columns.add(referencedColumn);
      }
    }
    return Array.from(columns);
  }, [inboundForeignKeys]);

  // Calculate offset and totalPages for pagination.
  // Outsize datasets are handled by windowed mode (auto-engaged when a single page exceeds
  // the in-memory threshold), so there's no longer a hard row cap on navigation.
  const currentPage = tabContent?.currentPage || 1;
  const pageSize = tabContent?.limit || 100;
  const offset = (currentPage - 1) * pageSize;
  // Some engines (Kusto) skip the expensive full-table count and report an
  // unknown total (undefined). derivePaginationModel then infers Next/Prev from
  // whether the current page came back full, so paging still works without a
  // total. SQL engines pass a real number and get exact pagination as before.
  // When the exact count missed its budget on an unfiltered fetch the backend
  // supplies `estimatedRowCount` (catalog statistics) instead — the model pages
  // off that and flags it so the UI can print it with a "≈".
  const paginationModel = derivePaginationModel({
    currentPage,
    pageSize,
    totalRowCount: tabContent?.totalRowCount,
    estimatedRowCount: tabContent?.estimatedRowCount,
    loadedRowCount: tabContent?.rows?.length ?? 0,
  });
  const totalRowCount = paginationModel.effectiveTotal;
  const totalPages = paginationModel.totalPages;

  // Fetch table data - use safe defaults when tab content is not available
  // Include PK columns even if hidden to ensure edit mode works correctly
  const liveTableData = useTableData({
    connectionId: connectionId || '',
    database: tabContent?.database || '',
    schema: effectiveSchema,
    tableName: effectiveTable,
    limit: pageSize,
    offset,
    columns: tabContent?.selectedColumns || '*',
    filters: effectiveFilters,
    sorting: effectiveSorting as SortDefinition[],
    // Disabled in blueprint-edit mode — rows come from the sidecar instead.
    // Also held while `pendingColumnReconcile` is set (open-from-pin): the
    // persisted filters/sorts/selected columns may reference columns that no
    // longer exist, so we wait for the reconcile pass (below) to prune them
    // and clear the flag before running the data query with stale SQL (G8).
    enabled:
      isValidTab &&
      !!connectionId &&
      !!effectiveTable &&
      !isDisconnected &&
      !isBlueprintEdit &&
      !tabContent?.pendingColumnReconcile,
    primaryKeyColumns: metadataPrimaryKeyColumns,
    alwaysIncludeColumns: inboundReferencedColumns,
    allColumnNames: metadataColumns.map((col) => col.name),
    tabId,
  });

  // Blueprint-edit mode: read rows from the `.data.json` sidecar and apply
  // filter/sort/paging client-side (small lookup tables). Mirrors the
  // `useTableData` return surface so the rest of the component is agnostic.
  const blueprintTableData = useBlueprintTableData({
    folderPath: blueprintEdit?.folderPath || '',
    schema: effectiveSchema,
    tableName: effectiveTable,
    limit: pageSize,
    offset,
    filters: effectiveFilters,
    sorting: effectiveSorting as SortDefinition[],
    enabled: isValidTab && isBlueprintEdit && !!effectiveTable,
    reloadToken: blueprintReloadToken,
  });

  const {
    data,
    isLoading,
    isFetching,
    error,
    refetch,
    populationProgress,
    currentExecutionIdRef,
    activeExecutionId,
  } = isBlueprintEdit ? blueprintTableData : liveTableData;

  // Sidecar-derived PK / identity column names (blueprint-edit mode). The
  // sidecar's mapped columns carry `isPrimaryKey` / `isIdentity` so editing
  // works even when the linked DB is disconnected and live metadata is empty.
  const blueprintPkColumns = useMemo(() => {
    if (!isBlueprintEdit) return [] as string[];
    return (blueprintTableData.data?.columns ?? [])
      .filter((c) => c.isPrimaryKey)
      .map((c) => c.name);
  }, [isBlueprintEdit, blueprintTableData.data]);
  const blueprintIdentityColumns = useMemo(() => {
    if (!isBlueprintEdit) return [] as string[];
    return (blueprintTableData.data?.columns ?? [])
      .filter((c) => c.isIdentity)
      .map((c) => c.name);
  }, [isBlueprintEdit, blueprintTableData.data]);

  // Effective PK / identity sets — metadata-first, sidecar fallback in
  // blueprint mode. Drives edit-mode WHERE clauses, the toolbar's
  // edit-disabled gate, and the column-enrichment effect below.
  const primaryKeyColumns = useMemo(() => {
    if (isBlueprintEdit && metadataPrimaryKeyColumns.length === 0) return blueprintPkColumns;
    return metadataPrimaryKeyColumns;
  }, [isBlueprintEdit, metadataPrimaryKeyColumns, blueprintPkColumns]);
  const hasPrimaryKey = primaryKeyColumns.length > 0;
  // Explicit capability gate — kusto (and any future append-only engine) must
  // never offer edit affordances, regardless of whether PK metadata happens to
  // be present. Previously this was only accidentally true for kusto because
  // it never surfaces PK metadata; this makes the read-only intent explicit.
  const engineSupportsEditing = engineSupports(engineId, 'tableExplorer', 'supportsEditing');
  // T6.1c (item 5) — `hasPrimaryKey && engineSupportsEditing` was spelled out
  // at four call sites (the grid's `editable`, plus `canQuickEdit` on three
  // separate row-details renders). T4.4 consolidated this shape elsewhere but
  // did not sweep this file. It is the same predicate every time — "rows on
  // this surface are editable at all" — and duplicating it means a future term
  // (another capability gate, another engine carve-out) has to be remembered
  // in four places, which is precisely how three of them end up drifting.
  const canEditRows = hasPrimaryKey && engineSupportsEditing;
  const effectivePkColumnsSet = useMemo(() => new Set(primaryKeyColumns), [primaryKeyColumns]);
  const effectiveIdentityColumnsSet = useMemo(
    () => computeEffectiveIdentityColumnsSet(isBlueprintEdit, identityColumnsSet, blueprintIdentityColumns),
    [isBlueprintEdit, identityColumnsSet, blueprintIdentityColumns],
  );

  // Streaming/windowed-mode state derived from the in-flight fetch.
  // `populationMode === 'windowed'` means the page exceeded the in-memory fast-path
  // threshold (~50k rows) — rows live in the SQLite cache, not in `data.rows`.
  const populationMode = data?.populationMode;
  const isWindowedMode = populationMode === 'windowed';
  // Quick edit (grid context menu, row-details panel, the record view's
  // `editable` and the single-row view) needs a SETTLED, IN-MEMORY page. On a
  // windowed page every text cell longer than 1 KB (`PREVIEW_MAX_BYTES`) is a
  // clipped preview, so saving one would write the preview back over the full value; edit mode is
  // force-exited on that transition below and `editDisabledKey` tells the user.
  // `data` alone is not enough: while the next page loads — or after its fetch
  // fails — `data` is undefined, `isWindowedMode` reads false, but the panels
  // still render the PREVIOUS page's rows from `tabContent.rows`, previews
  // included. Gating on a defined, non-windowed result closes that window
  // (reviewed 2026-09-17).
  const canQuickEditRows = canEditRows && data !== undefined && !isWindowedMode;

  // Enrich a raw result-set column list with the PK/FK/identity/type facts the
  // grid needs. Shared by the progressive-streaming path below and the settle
  // effect that persists columns into the tab content — the two must agree, or
  // the header changes shape when streaming hands over to the settled result.
  const enrichColumns = useCallback(
    (rawColumns: ReadonlyArray<{ name: string; dataType?: string; nullable?: boolean }>): TableExplorerColumn[] =>
      rawColumns.map((col) => {
        const normalizedName = normalizeColumnName(col.name);
        const metadataNullable = metadataNullableByNormalizedName.get(normalizedName);
        return {
          ...col,
          // Prefer metadata-derived type strings (often length-aware) over query result types.
          dataType: metadataDataTypeByNormalizedName.get(normalizedName) ?? col.dataType ?? '',
          // Metadata is authoritative for nullability — query result sets
          // don't carry it. Fall back to the result-set value when metadata
          // hasn't populated yet (initial fetch race).
          nullable: metadataNullable !== undefined ? metadataNullable : col.nullable ?? true,
          isPrimaryKey: effectivePkColumnsSet.has(col.name),
          isForeignKey: !!lookupForeignKey(foreignKeyMap, col.name),
          isIdentity: effectiveIdentityColumnsSet.has(col.name),
          isNativeEnumType: metadataIsNativeEnumByNormalizedName.get(normalizedName) ?? false,
        };
      }),
    [
      effectivePkColumnsSet,
      foreignKeyMap,
      effectiveIdentityColumnsSet,
      metadataDataTypeByNormalizedName,
      metadataIsNativeEnumByNormalizedName,
      metadataNullableByNormalizedName,
    ],
  );

  // Progressive hydration: while the IPC fetch is still in flight, render the rows that
  // have already streamed via `result-source:progress` instead of waiting for the queryFn
  // to settle. Mirrors the Query Editor's "rows arrive as they come" experience.
  //
  // `selectTableExplorerDisplaySource` owns the phase ladder (and its unit tests own the
  // two flicker regressions it exists to prevent); everything here just resolves the
  // chosen source into concrete rows + columns. Rows and columns MUST come from the SAME
  // source — the grid drops its skeleton the moment `rows` is non-empty, so a source with
  // rows but no columns paints a column-less grid.
  const persistedRows = tabContent?.rows;
  const persistedColumns = tabContent?.columns;
  const displaySource = selectTableExplorerDisplaySource({
    isFetching,
    ...(populationProgress
      ? {
          streamed: {
            state: populationProgress.state,
            mode: populationProgress.mode,
            rowCount: populationProgress.rows.length,
            columnCount: populationProgress.columns.length,
          },
        }
      : {}),
    persistedRowCount: persistedRows?.length ?? 0,
    hasSettledData: !!data,
  });
  const { rows: displayRows, columns: displayColumns } = useMemo<{
    rows: Record<string, unknown>[];
    columns: TableExplorerColumn[];
  }>(() => {
    switch (displaySource) {
      case 'streamed':
        return {
          rows: populationProgress!.rows,
          columns: enrichColumns(populationProgress!.columns),
        };
      case 'persisted':
        return { rows: persistedRows!, columns: persistedColumns ?? EMPTY_DISPLAY_COLUMNS };
      case 'settled':
        return { rows: data!.rows, columns: enrichColumns(data!.columns) };
      case 'pending':
        // Nothing to show — keep the previously persisted columns so the skeleton
        // lays out against the real header instead of a generic block.
        return { rows: EMPTY_DISPLAY_ROWS, columns: persistedColumns ?? EMPTY_DISPLAY_COLUMNS };
    }
  }, [displaySource, populationProgress, persistedRows, persistedColumns, data, enrichColumns]);

  // Spatial map pane (T14) — the loaded window IS the map's result set. Rows come
  // from the persisted loaded window (`tabContent.rows`, a stable ref) so the map
  // reflects paging/filter/sort. Columns carry the tab's schema/table as source
  // provenance so `getSpatialResultColumns` (the same detection T13 uses)
  // categorizes them identically to the panel.
  const spatialResultSet = useMemo<QueryResultSet | undefined>(() => {
    if (!tabContent?.columns?.length) return undefined;
    return {
      columns: tabContent.columns.map((col) => ({
        name: col.name,
        dataType: col.dataType,
        nullable: col.nullable,
        sourceSchema: effectiveSchema ?? undefined,
        sourceTable: effectiveTable,
        sourceColumn: col.name,
      })),
      rows: tabContent.rows ?? [],
    };
  }, [tabContent?.columns, tabContent?.rows, effectiveSchema, effectiveTable]);

  // Shared source for `hasSpatialColumn` AND the T19 declared-pair "Filter by
  // map view" lookup below — a single categorization pass instead of two.
  const spatialResultColumnsInfo = useMemo(
    () => getSpatialResultColumns(spatialResultSet, engineId, spatialMetaInfo),
    [spatialResultSet, engineId, spatialMetaInfo],
  );

  const hasSpatialColumn = useMemo(
    () => spatialResultColumnsInfo.spatialColumnIds.length > 0,
    [spatialResultColumnsInfo],
  );

  // Task 27 (A19/G18) — Chart builder exclusion set, derived from the same
  // categorization above rather than re-sniffed. Excludes native-spatial + declared
  // WKT/GeoJSON ids, but NOT declared lat/lng-PAIR ids: those key on the LAT column
  // for the map's "one logical cell" semantics, while the underlying lat/lng NUMERIC
  // columns are legitimate chart axes and must stay chartable.
  const chartSpatialColumnIds = useMemo(() => {
    const ids = new Set<string>();
    for (const id of spatialResultColumnsInfo.spatialColumnIds) {
      const decl = spatialResultColumnsInfo.declaredSpatialColumns.get(id);
      if (decl?.format === 'lat-lng-pair') continue;
      ids.add(id);
    }
    return ids;
  }, [spatialResultColumnsInfo]);

  // Close the map pane if the current table has no spatial column (e.g. after
  // switching tables via the breadcrumb) so a stale pane never lingers.
  useEffect(() => {
    if (!hasSpatialColumn && isMapPaneOpen) setIsMapPaneOpen(false);
  }, [hasSpatialColumn, isMapPaneOpen]);

  // Task 32 — spatial index coach, TE "cheap" non-plan trigger (A4/CD14): fires when a spatial
  // filter chip is active AND the loaded window hit the page cap, gated on `hasSpatialIndex(...)
  // !== true` (the anti-nag guard) so an already-indexed column never nags. TE has no execution
  // plan of its own — that's the OTHER (plan-based) trigger, wired into ExecutionPlanPanel.
  const activeSpatialFilter = useMemo(() => {
    for (const group of effectiveUserFilters) {
      const condition = group.conditions.find((c) => c.operator === 'spatial');
      if (condition) return { column: group.field, condition };
    }
    return null;
  }, [effectiveUserFilters]);

  const loadedRowCount = tabContent?.rows.length ?? 0;
  const spatialIndexCoachEligible =
    hasSpatialColumn && !!activeSpatialFilter && loadedRowCount > 0 && loadedRowCount >= pageSize;

  const [spatialIndexPresence, setSpatialIndexPresence] = useState<boolean | null>(null);
  useEffect(() => {
    const database = tabContent?.database;
    if (!spatialIndexCoachEligible || !connectionId || !activeSpatialFilter || !database) {
      setSpatialIndexPresence(null);
      return;
    }
    let cancelled = false;
    // `forceRefresh: true` — this effect only re-runs when the loaded window (`tabContent.rows`
    // reference, in the dep array below) genuinely changed, so a fresh probe here is cheap AND
    // is what lets a user who just created the suggested index elsewhere see the hint clear.
    void hasSpatialIndex(engineId, connectionId, database, effectiveSchema, effectiveTable, activeSpatialFilter.column, {
      forceRefresh: true,
    }).then((result) => {
      if (!cancelled) setSpatialIndexPresence(result);
    });
    return () => {
      cancelled = true;
    };
  }, [spatialIndexCoachEligible, connectionId, engineId, tabContent?.database, tabContent?.rows, effectiveSchema, effectiveTable, activeSpatialFilter]);

  const showSpatialIndexHint = spatialIndexCoachEligible && spatialIndexPresence !== true;

  const handleToggleMapPane = useCallback(() => {
    setIsMapPaneOpen((open) => {
      const next = !open;
      track('feature:spatial_map_view', { surface: 'table-explorer', engine: engineId, enabled: next });
      return next;
    });
  }, [track, engineId]);

  // Stop button on the inline status — cancels the in-flight fetch via the same
  // `cancel-query-v2` channel QE uses. Best-effort: server-side cancel may take a
  // moment to propagate; the button shows "Stopping…" until the fetch settles.
  // The button stays live while it says "Stopping…" so a second press escalates
  // to `force` — the fetch stops WAITING for the server to honour the polite
  // cancel and returns now (on Oracle the client socket is destroyed; the
  // server session finishes its own wait). Parity with the Query Editor's Stop.
  // WHICH execution is being cancelled, not merely "a cancel is outstanding".
  // `useTableData` claims `currentExecutionIdRef` for a replacement fetch the
  // moment the query key changes, and that can happen without an intervening
  // idle render — so a plain boolean both (a) escalated the first Stop on the
  // NEW fetch to `force`, and (b) advertised "Stopping… click again to force"
  // over a fetch that had received no cancel at all.
  const [cancellingExecutionId, setCancellingExecutionId] = useState<string | null>(null);
  // Which fetch the last Stop press targeted — the escalation key read by
  // `nextCancelRequest`. Separate from the state above because it must be
  // readable synchronously inside the callback, before any re-render.
  const lastCancelledExecutionIdRef = useRef<string | null>(null);
  const cancelFetch = useCallback(async () => {
    const executionId = currentExecutionIdRef.current;
    if (!executionId || !isFetching) return;
    const { request, force } = nextCancelRequest(executionId, lastCancelledExecutionIdRef.current);
    lastCancelledExecutionIdRef.current = executionId;
    setCancellingExecutionId(executionId);
    track('feature:table_explorer_cancel_fetch', { engine: engineId, force });
    try {
      await cancelQueryV2(request);
    } catch {
      // Best-effort: swallow errors from the cancel channel; the fetch will settle naturally.
    }
  }, [currentExecutionIdRef, isFetching, track, engineId]);
  // "Stopping…" belongs to the execution it was pressed for. Compared against
  // `activeExecutionId` — the hook's render-observable id — not the ref: React
  // Query renders the replacement fetch's query key BEFORE its `queryFn` writes
  // the ref, and a ref mutation re-renders nothing, so a ref-based comparison
  // kept showing the previous fetch's "Stopping…" over a fetch that had
  // received no cancel.
  const isCancelling = cancellingExecutionId != null && cancellingExecutionId === activeExecutionId;
  // Reset the "stopping" state — and the escalation key — once the fetch settles.
  useEffect(() => {
    if (isFetching) return;
    lastCancelledExecutionIdRef.current = null;
    if (cancellingExecutionId !== null) setCancellingExecutionId(null);
  }, [isFetching, cancellingExecutionId]);

  // Pending changes handler for edit mode. When a navigation-tree owner is
  // active (FK-breadcrumb is in play), route the write onto THAT node so
  // edits target the current view's table. Falls back to the tab-level
  // `pendingChanges` for legacy / pre-tree tabs.
  const handlePendingChangesUpdate = useCallback(
    (changes: PendingChange[]) => {
      if (activeEditOwnerId && navigationTreeForContext) {
        const nextTree = patchNodePendingChanges(
          navigationTreeForContext,
          activeEditOwnerId,
          changes,
        );
        if (nextTree) {
          updateTabContent(tabId, { navigationTree: nextTree });
          return;
        }
      }
      updateTabContent(tabId, { pendingChanges: changes });
    },
    [activeEditOwnerId, navigationTreeForContext, tabId, updateTabContent]
  );

  // Pending changes hook for edit mode
  // Use metadataColumns (stable full list) for INSERT - ensures all columns are available
  const {
    changeCount,
    addInsert,
    updateInsert,
    removeInsert,
    addUpdate,
    addDelete,
    removeDelete,
    discardAllChanges,
  } = usePendingChanges({
    pendingChanges,
    onChangesUpdate: handlePendingChangesUpdate,
    primaryKeyColumns,
    // In blueprint-edit mode the linked DB may be disconnected, so live
    // metadata columns can be empty — fall back to the grid's (sidecar-derived)
    // columns so Add Row still gets the full column set.
    columns: effectiveMetadataColumns,
  });

  // Get inserted rows for display
  const insertedRows = useMemo(
    () =>
      pendingChanges.filter(
        (c): c is PendingInsert => c.type === 'insert'
      ),
    [pendingChanges]
  );

  // Update tab content when data arrives
  // Enrich columns with PK/FK/identity info from metadata
  useEffect(() => {
    if (data && isValidTab) {
      updateTabContent(tabId, {
        rows: data.rows,
        // Enrich columns with PK/FK/identity info from metadata — the same pass
        // the streaming phase uses, so the header doesn't change shape at handover.
        columns: enrichColumns(data.columns),
        totalRowCount: data.totalRowCount,
        estimatedRowCount: readEstimatedRowCount(data),
        isLoading: false,
        error: undefined,
      });
    }
  }, [data, tabId, updateTabContent, isValidTab, enrichColumns]);

  // LOB columns from fetch result (Oracle) — used for full value fetch on cell expand
  const lobColumns = data?.lobColumns;

  // Fetch full cell value for LOB columns (Oracle)
  const handleFetchFullCellValue = useCallback(
    async (columnName: string, primaryKey: Record<string, unknown>): Promise<unknown> => {
      if (!connectionId || !tabContent?.tableName || tabContent?.schema === undefined) return undefined;
      const result = await fetchTableExplorerCellValue({
        connectionId,
        table: {
          database: tabContent.database || '',
          schema: tabContent.schema,
          tableName: tabContent.tableName,
        },
        columnName,
        primaryKey,
      });
      if (result.success) return result.value;
      return undefined;
    },
    [connectionId, tabContent?.database, tabContent?.schema, tabContent?.tableName]
  );

  // Track table explorer usage when table data is first loaded
  // Skip tracking for tabs restored from session (not user-initiated)
  useEffect(() => {
    if (!data || !isValidTab || !tabContent?.tableName) return;

    // Skip analytics for tabs restored from a previous session
    if (tabContent.restoredFromSession) return;

    // Create a unique key for this table
    const tableKey = `${connectionId}:${tabContent.database}:${tabContent.schema}:${tabContent.tableName}`;

    // Only track once per table
    if (hasTrackedRef.current === tableKey) return;
    hasTrackedRef.current = tableKey;

    track('feature:table_explorer', { engine: engineId });

    // Mark "any query has run" so the first-connect bridge never fires again
    // (a Table Explorer load runs the same capped SELECT the query editor would).
    markHasRunQuery();
  }, [data, isValidTab, tabContent?.tableName, tabContent?.database, tabContent?.schema, tabContent?.restoredFromSession, connectionId, engineId, track]);

  // Update loading state
  useEffect(() => {
    if (isValidTab) {
      updateTabContent(tabId, { isLoading });
    }
  }, [isLoading, tabId, updateTabContent, isValidTab]);

  // Update error state
  useEffect(() => {
    if (error && isValidTab) {
      // Slice 6 — when the row fetch failed with a structured `isDatabaseError`,
      // surface a per-tab connection-failure banner with the SQL error code so
      // the user can open scoped diagnostics. Plain string `error` is preserved
      // for any existing toasts/inline error renderers.
      const details = (error as Error & {
        details?: { isDatabaseError?: boolean; sqlErrorCode?: number; originalMessage?: string };
      }).details;
      const message = error instanceof Error ? error.message : String(error);
      const isDbFailure = details?.isDatabaseError === true;
      updateTabContent(tabId, {
        error: message,
        isLoading: false,
        connectionFailure: isDbFailure
          ? {
              message,
              ...(details?.sqlErrorCode !== undefined ? { sqlErrorCode: details.sqlErrorCode } : {}),
              ...(tabContent?.database ? { scopeDatabase: tabContent.database } : {}),
            }
          : undefined,
      });
    }
  }, [error, tabId, updateTabContent, isValidTab, tabContent?.database]);

  // Slice 6 — clear the per-tab connection-failure banner once a fetch
  // succeeds against the scope database. Keyed by `data` (TanStack Query's
  // success indicator) and `isFetching` so we only run on settled success.
  useEffect(() => {
    if (data && !isFetching && isValidTab && tabContent?.connectionFailure) {
      updateTabContent(tabId, { connectionFailure: undefined });
    }
  }, [data, isFetching, isValidTab, tabContent?.connectionFailure, tabId, updateTabContent]);

  // Handle page size change (resets to page 1)
  const handlePageSizeChange = useCallback((newPageSize: number) => {
    updateTabContent(tabId, { limit: newPageSize, currentPage: 1 });
  }, [tabId, updateTabContent]);

  // Handle page change
  const handlePageChange = useCallback((newPage: number) => {
    updateTabContent(tabId, { currentPage: newPage });
  }, [tabId, updateTabContent]);

  // Handle refresh
  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleSelectRow = useCallback((args: { rowIndex: number; row: Record<string, unknown> }) => {
    setSelectedRowIndex(args.rowIndex);
    setRowDetailsRowIndex((prev) => (prev === null ? null : args.rowIndex));
  }, []);

  const handleToggleRowDetails = useCallback((args: { rowIndex: number; row: Record<string, unknown> }) => {
    setSelectedRowIndex(args.rowIndex);
    setRowDetailsLayout('bottom');
    setRowDetailsRowIndex((prev) => (rowDetailsLayout === 'bottom' && prev === args.rowIndex ? null : args.rowIndex));
  }, [rowDetailsLayout]);

  // FK Breadcrumb (Slice 3) — navigation tree + active node drive the view.
  const navTree = useTabNavigationTree(tabId);

  // Slice 6 — tab title follows the active node in the breadcrumb tree.
  // We only push when the computed title differs from the current one and
  // the user hasn't set a custom title via the rename UI (`customTitle`).
  useEffect(() => {
    if (!isValidTab || !currentTab) return;
    if (currentTab.customTitle) return;
    const nextTitle = computeTableExplorerTabTitle({
      tree: navTree.tree,
      fallbackTable: tabContent?.tableName ?? '',
    });
    if (nextTitle && nextTitle !== currentTab.title) {
      useWorkspaceStore.getState().updateTabTitle(tabId, nextTitle, false);
    }
  }, [
    isValidTab,
    currentTab,
    navTree.tree,
    tabContent?.tableName,
    tabId,
  ]);

  // Slice 5 — invalidator for the `joinTableHints` MetaInfo entries the chip
  // context menu writes. The `useMetaInfo` query already feeds the renderer
  // elsewhere; the breadcrumb only needs to invalidate after a mutate.
  const invalidateMetaInfo = useInvalidateMetaInfo();

  // Task 5.2 — after a committed row mutation, refresh every lookup enum whose
  // labels are READ FROM the table just edited (rename a status row → the
  // order grid's `3 · Active` follows). Self-noops when nothing looks the
  // edited table up, which is nearly every edit.
  const refreshLookupCachesForEditedTable = useLookupCacheWriteThrough({
    connectionId,
    database: tabContent?.database,
    engine: engineId,
  });

  // Slice 5 — MetaInfo snapshot for the current (connection, database). We
  // read `joinTableHints` from it to honor user overrides when computing
  // `isJoinCollapsed` for the row-drilled table-node below.
  const { data: metaInfoForJoinHints } = useMetaInfo(
    connectionId ?? null,
    tabContent?.database ?? null,
  );

  // Slice 1 (FK Breadcrumb / EP2) — push a row-node onto the navigation tree.
  // PK columns must be known; the caller (TableExplorerGrid) already gates the
  // "Open record details" menu item on `primaryKeyColumns.length > 0`.
  const handleOpenRecordDetails = useCallback(
    (args: { row: Record<string, unknown> }) => {
      if (primaryKeyColumns.length === 0) return;
      if (!tabContent) return;
      const pk = primaryKeyColumns.map((col) => ({ column: col, value: args.row[col] }));
      const treeWasEmpty = navTree.treeWasEmpty;
      navTree.ensureRoot({
        schema: tabContent.schema,
        table: tabContent.tableName,
        filters: [],
      });
      navTree.pushRow({ schema: tabContent.schema, table: tabContent.tableName, pk });
      track('feature:fk_nav_drilldown', {
        engine: engineId,
        source: 'row-context',
        targetKind: 'row',
        direction: 'same-row',
        treeWasEmpty,
        viaLooseFk: false,
        joinCollapsed: false,
      });
    },
    [primaryKeyColumns, tabContent, navTree, track, engineId],
  );

  // Back-to-grid handler — used by the `<SingleRowView />` header.
  // Navigate to the parent of the active row-node (typically the table-node
  // the row was drilled into from). The breadcrumb stays — its `×` button is
  // what removes the row-node from the tree. Falls back to clearing only if
  // there is no parent (defensive — a row-node always has one in practice).
  const handleCloseSingleRow = useCallback(() => {
    const active = navTree.activeNode;
    if (active?.parentId) {
      navTree.activateNode(active.parentId);
      return;
    }
    navTree.clearTree();
  }, [navTree]);

  // Derive the active row-node ref (if any) for the SingleRowView.
  const singleRowMatch = useMemo(() => {
    const active = navTree.activeNode;
    if (!active || active.kind !== 'row') return null;
    if (!active.pk.length) return null;
    const match = (tabContent?.rows ?? []).find((row) =>
      active.pk.every((p) => row[p.column] === p.value),
    );
    if (!match) return null;
    return { ref: active, row: match };
  }, [navTree.activeNode, tabContent?.rows]);

  // Cross-table / out-of-window SRV. The PK-fetch SRV branch fires whenever
  // the active node is a row-node and `singleRowMatch` couldn't find that PK
  // inside the loaded grid window. This covers two cases that both need an
  // explicit PK lookup via `useSingleRowData`:
  //  1. The active row belongs to a DIFFERENT `{schema,table}` than the grid
  //     (true cross-table drill — e.g. drilled `Product → BillOfMaterials →
  //     Product#803` and we're now back on a Product row that wasn't in the
  //     in-memory window).
  //  2. The active row belongs to the SAME table but its PK isn't in
  //     `tabContent.rows` (the row is on page 3+ of a paged tab, or the tab
  //     loaded a windowed subset). `singleRowMatch` returns null in that
  //     case; without this branch the UI would silently fall back to the
  //     grid for any FK that resolved to a row past the loaded window.
  // The "table-equal-but-out-of-window" case (#2) is the one user-reported on
  // 2026-05-25: drilling `Product → BillOfMaterials → ProductAssemblyID=803`
  // on a 504-row Product tab opened the full grid instead of the SRV because
  // #803 sat past the first 200-row page.
  const activeRowNode = useMemo<SingleRowNodeRef | null>(() => {
    const active = navTree.activeNode;
    if (!active || active.kind !== 'row') return null;
    if (!active.pk.length) return null;
    return { kind: 'row', schema: active.schema, table: active.table, pk: active.pk };
  }, [navTree.activeNode]);

  const isCrossTableActive = !!(activeRowNode && !singleRowMatch && tabContent);

  // Single source of truth for whether the pagination strip renders — the
  // strip is meaningless in cross-table drill and single-row modes (there's
  // no page to navigate). `TableExplorerStatusBar` needs the exact negation
  // of this so it can pick up the row count when the strip isn't there to
  // carry it (see the status bar's own doc comment) — derive both sites from
  // this one value rather than duplicating the boolean expression.
  const showPaginationStrip = !(isCrossTableActive && activeRowNode) && !singleRowMatch;

  // Shared `enabled` gate for the three cross-table queries below —
  // `useSingleRowData` itself, the cross-table inbound-FK fetch, and the
  // cross-table table-metadata fetch. Re-derives whenever any of the
  // underlying selectors change.
  const crossTableEnabled =
    isCrossTableActive &&
    !!connectionId &&
    !!tabContent?.database &&
    !!activeRowNode?.table;

  // Hook is always called; `enabled` gates the actual fetch. When the
  // active row-node points at the underlying grid table we keep using
  // `singleRowMatch` instead (cheaper, no extra IPC).
  // Provide a stable placeholder node so the hook always receives a
  // SingleRowNodeRef-shaped value while idle.
  const placeholderNode = useMemo<SingleRowNodeRef>(
    () => ({ kind: 'row', schema: '', table: '', pk: [] }),
    [],
  );
  const crossTableRowQuery = useSingleRowData({
    engine: engineId,
    connectionId: connectionId ?? '',
    database: tabContent?.database ?? '',
    node: activeRowNode ?? placeholderNode,
    enabled: crossTableEnabled,
  });

  // Inbound FKs / table metadata for the ACTIVE (cross-table) row node —
  // gated so we don't hit the IPC layer when not cross-table.
  const crossTableInboundFksQuery = useInboundForeignKeys({
    connectionId: connectionId || '',
    database: tabContent?.database || '',
    schema: activeRowNode?.schema ?? '',
    tableName: activeRowNode?.table ?? '',
    enabled: crossTableEnabled && supportsInboundForeignKeyExplorer,
  });
  const crossTableInboundFks = crossTableInboundFksQuery.data || EMPTY_INBOUND_FOREIGN_KEYS;

  const crossTableMetadata = useTableMetadata({
    connectionId: connectionId || '',
    database: tabContent?.database || '',
    schema: activeRowNode?.schema ?? '',
    tableName: activeRowNode?.table ?? '',
    enabled: crossTableEnabled,
  });

  // FK Breadcrumb — handlers for the chip-row.
  // Optimistic chip "active" feedback lives inside `FkBreadcrumb` itself
  // (small render scope). The container calls back into here with the
  // already-rAF-deferred mutation, so we can update the store synchronously.
  //
  // Per-node edit mode (FK-breadcrumb follow-up): each TableNode owns its
  // own `isEditMode` + `pendingChanges`. Activating a different chip simply
  // swaps which node's edit-state is active — neither node loses its edits.
  const handleChipActivate = useCallback(
    (nodeId: string) => {
      navTree.activateNode(nodeId);
    },
    [navTree],
  );
  // Dismiss confirmation — when the dismissed chip's subtree contains
  // unsaved edits we show a Save / Discard / Cancel dialog before nuking
  // them. Cancel leaves the tree untouched; Discard proceeds with dismiss;
  // Save commits edits then proceeds with dismiss.
  const [pendingDismissId, setPendingDismissId] = useState<string | null>(null);
  const handleChipDismiss = useCallback(
    (nodeId: string) => {
      const tree = navTree.tree;
      if (tree && subtreeHasPendingChanges(tree, nodeId)) {
        setPendingDismissId(nodeId);
        return;
      }
      navTree.dismissNode(nodeId);
    },
    [navTree],
  );
  const handleConfirmDismissDiscard = useCallback(() => {
    const id = pendingDismissId;
    setPendingDismissId(null);
    if (id) navTree.dismissNode(id);
  }, [pendingDismissId, navTree]);
  const handleConfirmDismissCancel = useCallback(() => {
    setPendingDismissId(null);
  }, []);
  const pendingDismissChangeCount = useMemo(() => {
    if (!pendingDismissId || !navigationTreeForContext) return 0;
    const stack: string[] = [pendingDismissId];
    let n = 0;
    while (stack.length > 0) {
      const id = stack.pop();
      if (id === undefined) break;
      const node = navigationTreeForContext.nodes[id];
      if (!node) continue;
      if (node.kind === 'table') n += node.pendingChanges?.length ?? 0;
      for (const cid of node.childIds) stack.push(cid);
    }
    return n;
  }, [pendingDismissId, navigationTreeForContext]);

  // Slice 5 — set / clear the per-table MetaInfo `joinTableHint`. Called from
  // the breadcrumb chip's right-click context menu ("Always show / Always
  // collapse / Use auto"). Fire-and-forget; invalidate the metainfo query so
  // the next render picks up the new hint.
  const handleSetJoinTableHint = useCallback(
    ({ schema, table, hint }: { schema: string; table: string; hint: JoinTableHint | null }) => {
      const db = tabContent?.database;
      if (!connectionId || !db) return;
      const api = window.electronAPI?.metainfo;
      if (!api) return;
      const now = new Date().toISOString();
      const op =
        hint === null
          ? api.removeJoinTableHint(connectionId, db, schema, table)
          : api.upsertJoinTableHint(connectionId, db, {
              schema,
              table,
              hint,
              updatedAt: now,
            });
      op.then(() => {
        invalidateMetaInfo(connectionId, db);
      }).catch(() => {
        // Best-effort; surfaces via the next refetch.
      });
    },
    [connectionId, tabContent?.database, invalidateMetaInfo],
  );

  // Slice 3 review: `ForeignKeyPopover` now reads `fkOpenDefault` directly
  // via `useAppStore` and instantiates `useFkBreadcrumbFirstRunHint()` on
  // its own — the window-global trampolines were removed.

  // Handle filters change (resets page to 1). When the breadcrumb's active
  // node is a non-root TableNode, the user-added filters live ON that node
  // (so they survive reactivation); otherwise they live in tabContent as
  // before.
  const handleFiltersChange = useCallback(
    (newFilters: FilterGroup[]) => {
      if (isOnNonRootTable) {
        navTree.setActiveUserFilters(newFilters);
        updateTabContent(tabId, { currentPage: 1 });
        return;
      }
      updateTabContent(tabId, { filters: newFilters, currentPage: 1 });
    },
    [tabId, updateTabContent, isOnNonRootTable, navTree]
  );

  // Handle selected columns change
  const handleSelectedColumnsChange = useCallback(
    (newSelectedColumns: string[] | '*') => {
      updateTabContent(tabId, { selectedColumns: newSelectedColumns });
    },
    [tabId, updateTabContent]
  );

  // ─── T15/T19 — spatial filter chips from the map pane ────────────────────
  // The first native-spatial column + its kind (geography vs geometry) for the
  // "Filter by map view" bbox chip and the map-click distance point. T19: when
  // there is no native-spatial column, falls back to the first DECLARED lat/
  // lng-PAIR column — the numeric-range bbox emit needs no native geometry at
  // all, so "Filter by map view" works on ANY engine for a declared pair.
  // Declared WKT/GeoJSON/latlng-text columns are excluded (no sibling numeric
  // range to filter on); they still render on the map, just aren't filterable
  // by viewport.
  type SpatialFilterColumnInfo = { name: string; kind: SpatialFilterContext['kind']; lngColumn?: string };
  const spatialFilterColumn = useMemo<SpatialFilterColumnInfo | null>(() => {
    const name = [...nativeSpatialColumnNames][0];
    if (name) {
      const col = tabContent?.columns.find((c) => c.name === name);
      const kind: SpatialFilterContext['kind'] = /^\s*geography/i.test(col?.dataType ?? '')
        ? 'geography'
        : 'geometry';
      return { name, kind };
    }
    for (const [colId, decl] of spatialResultColumnsInfo.declaredSpatialColumns) {
      if (decl.format === 'lat-lng-pair' && decl.lngColumn) {
        return { name: colId, kind: 'declared-pair', lngColumn: decl.lngColumn };
      }
    }
    return null;
  }, [nativeSpatialColumnNames, tabContent?.columns, spatialResultColumnsInfo]);

  // "Filter by current map view" → add/replace an intersects-bbox chip for the
  // spatial column, sourcing the SRID from the decoded window (>1 distinct SRID
  // ⇒ null, never a guess). Bbox comes from T14's viewport state verbatim (the
  // emit owns any antimeridian split). A declared-pair column (T19) always
  // stamps SRID 4326 (WGS84, per `decodeDeclaredSpatialValue`) — the numeric
  // range emit doesn't consult it, but the context stays factually accurate.
  const handleFilterByMapView = useCallback(() => {
    const col = spatialFilterColumn;
    if (!col || !spatialViewport) return;
    const spatialContext: SpatialFilterContext =
      col.kind === 'declared-pair'
        ? { kind: 'declared-pair', srid: 4326, lngColumn: col.lngColumn }
        : { kind: col.kind, srid: spatialColumnSrids.length === 1 ? spatialColumnSrids[0] : null };
    const condition: FilterCondition = {
      operator: 'spatial',
      spatialSubOperator: 'intersects_bbox',
      bbox: spatialViewport.bbox,
      spatialContext,
    };
    const others = effectiveUserFilters.filter(
      (g) =>
        !(
          g.field === col.name &&
          g.conditions.some((c) => c.operator === 'spatial' && c.spatialSubOperator === 'intersects_bbox')
        ),
    );
    handleFiltersChange([...others, { field: col.name, conditions: [condition] }]);
    track('feature:spatial_filter_apply', { kind: 'bbox', engine: engineId });
  }, [spatialFilterColumn, spatialViewport, spatialColumnSrids, effectiveUserFilters, handleFiltersChange, track, engineId]);

  // Map click → set the centre of the first incomplete within-distance chip
  // ("arm" the distance filter). No-op when no such chip is waiting.
  const handleSpatialMapClick = useCallback(
    (lngLat: [number, number]) => {
      let armed = false;
      const next = effectiveUserFilters.map((g) => ({
        ...g,
        conditions: g.conditions.map((c) => {
          if (!armed && c.operator === 'spatial' && c.spatialSubOperator === 'within_distance' && !c.point) {
            armed = true;
            return { ...c, point: lngLat };
          }
          return c;
        }),
      }));
      if (armed) {
        handleFiltersChange(next);
        track('feature:spatial_filter_apply', { kind: 'distance', engine: engineId });
      }
    },
    [effectiveUserFilters, handleFiltersChange, track, engineId],
  );

  // Draw-a-polygon "in area" filter (T15). Owns its own draw state + chip-apply; the map pane below
  // receives its `draw` contract and renders the "Draw area" button when the column/engine supports it.
  const spatialDraw = useSpatialDrawFilter({
    spatialFilterColumn,
    spatialColumnSrids,
    userFilters: effectiveUserFilters,
    onFiltersChange: handleFiltersChange,
    engineId,
    track,
  });

  // Handle column reorder from the grid header drag. The grid hands us the
  // new order of currently-visible columns; persist it as an explicit array
  // so the order survives across app restarts. (Analytics fire inside the
  // grid where the actual drag-end is observed.)
  const handleColumnReorder = useCallback(
    (newOrder: string[]) => {
      updateTabContent(tabId, { selectedColumns: newOrder });
    },
    [tabId, updateTabContent]
  );

  // Handle sorting change (resets page to 1). Routed onto the breadcrumb's
  // active node when on a non-root TableNode.
  const handleSortingChange = useCallback(
    (newSorting: SortDefinition[]) => {
      if (isOnNonRootTable) {
        navTree.setActiveUserSorting(newSorting);
        updateTabContent(tabId, { currentPage: 1 });
        return;
      }
      updateTabContent(tabId, { sorting: newSorting, currentPage: 1 });
    },
    [tabId, updateTabContent, isOnNonRootTable, navTree]
  );

  // Track pending draft columns (from toolbar "Add" buttons)
  const [pendingFilterColumn, setPendingFilterColumn] = useState<string | null>(null);
  const [pendingSortColumn, setPendingSortColumn] = useState<string | null>(null);
  // Pre-filled draft filter (from "Add to filter" cell context menu) — the user
  // must confirm with Enter / Apply for the filter to actually apply.
  const [pendingDraftFilter, setPendingDraftFilter] = useState<FilterGroup | null>(null);
  // Mirrors FilterWizard's local draft state so external "Add Filter" entry
  // points (e.g. the toolbar picker) can gate their popovers when a dirty
  // draft is pending.
  const [filterWizardHasDraft, setFilterWizardHasDraft] = useState(false);
  // Increment to ask FilterWizard to pulse + tooltip the dirty draft chip.
  const [filterPulseRequest, setFilterPulseRequest] = useState(0);

  // Handle adding a filter from toolbar - sets pending draft, doesn't apply immediately
  const handleAddFilter = useCallback(
    (columnName: string) => {
      setPendingFilterColumn(columnName);
    },
    []
  );

  // Toolbar's Add Filter picker click target when a draft is already dirty:
  // pulse the chip + tooltip instead of opening the picker.
  const handleAddFilterBlocked = useCallback(() => {
    setFilterPulseRequest((t) => t + 1);
  }, []);

  // Handle adding a filter with a specific value (from cell context menu).
  // The new filter is created as a *draft* — it's not applied until the user
  // confirms with Enter / the Apply button. Each click stacks a new filter on
  // the column (multiple filters per column are AND-ed in the SQL builder).
  // Operator is chosen by `pickCellFilterOperator` (IS NULL for null, = otherwise).
  const handleAddFilterWithValue = useCallback(
    (columnName: string, value: unknown) => {
      // Look up the column metadata so FK columns default to the lookup operator
      // (renders the row picker instead of a free-text input) — the column
      // itself, never one whose name only normalizes alike (`"ref-id"`).
      const targetColumn = findColumnByName(effectiveMetadataColumns, columnName);
      const choice = pickCellFilterOperator(value, targetColumn);
      const draft: FilterGroup = {
        field: columnName,
        conditions: [
          choice.value === undefined
            ? { operator: choice.operator }
            : { operator: choice.operator, value: choice.value },
        ],
      };
      setPendingDraftFilter(draft);
      track('feature:te_add_column_to_filter', {
        engine: engineId,
        operator: choice.operator,
        hadValue: choice.value !== undefined,
      });
    },
    [effectiveMetadataColumns, track, engineId]
  );

  // Handle adding a sort from toolbar - sets pending draft, doesn't apply immediately
  const handleAddSort = useCallback(
    (columnName: string) => {
      setPendingSortColumn(columnName);
    },
    []
  );

  // Clear pending filter when it's been handled (draft committed or cancelled).
  // Clears both the toolbar-driven empty draft and the cell-menu-driven pre-filled draft.
  const handleClearPendingFilter = useCallback(() => {
    setPendingFilterColumn(null);
    setPendingDraftFilter(null);
  }, []);

  // Clear pending sort when it's been handled
  const handleClearPendingSort = useCallback(() => {
    setPendingSortColumn(null);
  }, []);

  // Get columns that already have filters/sorts (for disabling in toolbar selector)
  const filteredColumns = useMemo(
    () => (tabContent?.filters || []).map((f) => f.field),
    [tabContent?.filters]
  );
  const sortedColumns = useMemo(
    () => (tabContent?.sorting || []).map((s) => s.field),
    [tabContent?.sorting]
  );

  // Handle preview query button click
  //
  // Use the EFFECTIVE schema/table/filters/sorting — these track the active
  // FK-breadcrumb node, so a drilled child table previews its own SELECT,
  // not the root tab's. The grid + toolbar table-name already use these
  // effective values; the preview had been falling back to raw `tabContent`
  // (root) and rendering the wrong SQL when the breadcrumb was active.
  const handlePreviewQuery = useCallback(async () => {
    if (!connectionId || !tabContent?.database || !effectiveTable) return;

    setPreviewQuery('-- Loading...');
    setIsQueryPreviewOpen(true);

    try {
      const result = await previewTableExplorerSelect({
        connectionId,
        table: {
          database: tabContent.database,
          schema: effectiveSchema,
          tableName: effectiveTable,
        },
        columns: tabContent.selectedColumns,
        limit: pageSize,
        offset,
        filters: effectiveFilters,
        sorting: effectiveSorting as SortDefinition[],
        primaryKeyColumns,
      });

      if (result.success) {
        setPreviewQuery(result.sql || '');
        return;
      }

      // Connection lost via result-level error (IPC wraps throws into {success:false})
      if (connectionId) {
        const friendlyMsg = checkAndQueueConnectionLostRetry(result.error, {
          tabId,
          connectionId,
          label: 'preview',
          action: () => handlePreviewQueryRef.current(),
        });
        if (friendlyMsg !== null) {
          setPreviewQuery(`-- ${friendlyMsg}`);
          return;
        }
      }

      setPreviewQuery(`-- Failed to generate preview: ${result.error || 'Unknown error'}`);
    } catch (error) {
      // Connection lost via thrown exception (rare — IPC layer normally wraps)
      if (connectionId) {
        const friendlyMsg = checkAndQueueConnectionLostRetry(error, {
          tabId,
          connectionId,
          label: 'preview',
          action: () => handlePreviewQueryRef.current(),
        });
        if (friendlyMsg !== null) {
          setPreviewQuery(`-- ${friendlyMsg}`);
          return;
        }
      }
      setPreviewQuery(`-- Failed to generate preview: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }, [connectionId, tabContent?.database, effectiveSchema, effectiveTable, tabContent?.selectedColumns, pageSize, offset, effectiveFilters, effectiveSorting, primaryKeyColumns]);

  // S20 — pin the CURRENT table SELECT (columns/filters/sorting, page-1
  // window) to a dashboard. The SELECT is generated on demand by the same
  // preview service the "Preview query" dialog uses — there is no cached
  // last-executed SQL on this surface.
  // Pin to dashboard is a Dashboards surface — it disappears with the
  // feature rather than pinning into a workspace the user can't open.
  const dashboardsBetaEnabled = useBetaFeature('dashboards');

  const handlePinToDashboard = useCallback(async () => {
    if (!connectionId || !tabContent?.database || !effectiveTable) return;
    const result = await previewTableExplorerSelect({
      connectionId,
      table: {
        database: tabContent.database,
        schema: effectiveSchema,
        tableName: effectiveTable,
      },
      columns: tabContent.selectedColumns,
      limit: pageSize,
      offset: 0,
      filters: effectiveFilters,
      sorting: effectiveSorting as SortDefinition[],
      primaryKeyColumns,
    });
    if (!result.success || !result.sql) {
      toast.error({ title: 'Could not build the table query', description: result.error });
      return;
    }
    requestPinToDashboard({
      from: 'table-explorer',
      sql: result.sql,
      connectionId,
      database: tabContent.database,
      titleHint: effectiveSchema ? `${effectiveSchema}.${effectiveTable}` : effectiveTable,
    });
  }, [connectionId, tabContent?.database, tabContent?.selectedColumns, effectiveSchema, effectiveTable, pageSize, effectiveFilters, effectiveSorting, primaryKeyColumns]);

  const handleOpenInQueryEditor = useCallback(
    (query: string) => {
      const { addTab, getNextQueryNumber } = useWorkspaceStore.getState();
      const queryNumber = getNextQueryNumber();

      addTab({
        title: `Query-${queryNumber}`,
        type: 'query',
        content: { query },
        connectionInfo: queryConnectionInfo,
      });
    },
    [queryConnectionInfo]
  );

  // Handle table selection from TableSelector
  const handleTableSelect = useCallback(
    (table: TableInfo) => {
      updateTabContent(tabId, {
        tableName: table.name,
        schema: table.schema,
        database: table.database,
      });
      // Update tab title to include table name
      useWorkspaceStore.getState().updateTabTitle(tabId, `${table.name} [Edit]`);
    },
    [tabId, updateTabContent]
  );

  // Handle database change from TableSelector
  const handleDatabaseChange = useCallback(
    (newDatabase: string) => {
      updateTabContent(tabId, {
        database: newDatabase,
        // Reset table-specific content when database changes
        tableName: '',
        schema: defaultSchema,
      });
    },
    [tabId, updateTabContent, defaultSchema]
  );

  // ========== Edit Mode Handlers ==========

  // Per-node-aware edit-state writer. Mirrors `handlePendingChangesUpdate`'s
  // routing: writes onto the active TableNode in the breadcrumb tree when
  // available, otherwise to the tab-level state for legacy tabs.
  const writeEditState = useCallback(
    (patch: { isEditMode?: boolean; pendingChanges?: PendingChange[] }) => {
      if (activeEditOwnerId && navigationTreeForContext) {
        let nextTree = navigationTreeForContext;
        if (patch.isEditMode !== undefined) {
          const t = patchNodeEditMode(nextTree, activeEditOwnerId, patch.isEditMode);
          if (t) nextTree = t;
        }
        if (patch.pendingChanges !== undefined) {
          const t = patchNodePendingChanges(
            nextTree,
            activeEditOwnerId,
            patch.pendingChanges,
          );
          if (t) nextTree = t;
        }
        if (nextTree !== navigationTreeForContext) {
          updateTabContent(tabId, { navigationTree: nextTree });
          return;
        }
      }
      updateTabContent(tabId, patch);
    },
    [activeEditOwnerId, navigationTreeForContext, tabId, updateTabContent],
  );

  // Toggle edit mode on/off — operates on the active per-node owner (root or
  // drilled TableNode). RowNodes have no edit-state owner so this is a no-op
  // when called against them (the toolbar isn't visible in that case anyway).
  const handleToggleEditMode = useCallback(() => {
    const isCurrentlyInEditMode = effectiveIsEditMode;

    // If exiting edit mode with pending changes, show confirmation dialog
    if (isCurrentlyInEditMode && changeCount > 0) {
      setIsExitEditModeOpen(true);
      return;
    }

    // Otherwise, toggle directly
    const newEditMode = !isCurrentlyInEditMode;
    writeEditState({
      isEditMode: newEditMode,
      pendingChanges: newEditMode ? [...pendingChanges] : [],
    });
  }, [effectiveIsEditMode, changeCount, pendingChanges, writeEditState]);

  // Exit edit mode after saving (called from exit dialog)
  const handleExitAfterSave = useCallback(() => {
    setExitAfterSave(true);
    setIsSaveChangesOpen(true);
  }, []);

  // Exit edit mode after discarding (called from exit dialog)
  const handleExitAfterDiscard = useCallback(() => {
    discardAllChanges();
    writeEditState({
      isEditMode: false,
      pendingChanges: [],
    });
  }, [discardAllChanges, writeEditState]);

  // Force-exit edit mode if the page transitions to windowed (streamed) mode.
  // In windowed mode rows aren't fully materialized in the renderer, so editing
  // can't safely build per-row WHERE clauses. Pending changes are dropped because
  // we can't reconcile them against rows that aren't loaded.
  useEffect(() => {
    if (!isWindowedMode) return;
    if (!isValidTab) return;
    if (!effectiveIsEditMode && pendingChanges.length === 0) return;
    discardAllChanges();
    writeEditState({ isEditMode: false, pendingChanges: [] });
  }, [isWindowedMode, isValidTab, effectiveIsEditMode, pendingChanges.length, discardAllChanges, writeEditState]);

  // Add a new row (for full edit mode)
  const handleAddRow = useCallback(() => {
    addInsert();
  }, [addInsert]);

  // Open save changes dialog (for full edit mode)
  const handleOpenSaveChanges = useCallback(() => {
    setIsSaveChangesOpen(true);
  }, []);

  // Discard all pending changes (for full edit mode)
  const handleDiscardChanges = useCallback(() => {
    discardAllChanges();
  }, [discardAllChanges]);

  // Cell edit handler (for full edit mode)
  const handleCellEdit = useCallback(
    (primaryKey: Record<string, unknown>, field: string, oldValue: unknown, newValue: unknown) => {
      addUpdate(primaryKey, field, oldValue, newValue);
    },
    [addUpdate]
  );

  // Insert cell edit handler (for full edit mode)
  const handleInsertCellEdit = useCallback(
    (tempId: string, field: string, value: unknown) => {
      updateInsert(tempId, field, value);
    },
    [updateInsert]
  );

  // Delete row handler (for full edit mode)
  const handleDeleteRow = useCallback(
    (primaryKey: Record<string, unknown>, originalRow: Record<string, unknown>) => {
      addDelete(primaryKey, originalRow);
    },
    [addDelete]
  );

  // Undo delete handler (for full edit mode)
  const handleUndoDelete = useCallback(
    (primaryKey: Record<string, unknown>) => {
      removeDelete(primaryKey);
    },
    [removeDelete]
  );

  // Remove insert handler (for full edit mode)
  const handleRemoveInsert = useCallback(
    (tempId: string) => {
      removeInsert(tempId);
    },
    [removeInsert]
  );

  // Quick edit handler (for context menu - single cell edit)
  const handleQuickEdit = useCallback(
    (primaryKey: Record<string, unknown>, field: string, oldValue: unknown, newValue: unknown) => {
      if (quickEditCloseTimeoutRef.current) {
        clearTimeout(quickEditCloseTimeoutRef.current);
        quickEditCloseTimeoutRef.current = null;
      }
      setQuickEditData({ primaryKey, field, oldValue, newValue });
      setIsQuickEditOpen(true);
    },
    []
  );

  const handleQuickEditOpenChange = useCallback((open: boolean) => {
    setIsQuickEditOpen(open);

    if (open) return;

    // Keep dialog mounted briefly after close so Radix can clean up modal layers
    // (prevents stuck backdrop / pointer-events issues).
    if (quickEditCloseTimeoutRef.current) {
      clearTimeout(quickEditCloseTimeoutRef.current);
    }
    quickEditCloseTimeoutRef.current = setTimeout(() => {
      setQuickEditData(null);
      quickEditCloseTimeoutRef.current = null;
    }, 250);
  }, []);

  useEffect(() => {
    return () => {
      if (quickEditCloseTimeoutRef.current) {
        clearTimeout(quickEditCloseTimeoutRef.current);
        quickEditCloseTimeoutRef.current = null;
      }
    };
  }, []);

  // Execute quick edit (submit single cell change)
  // Uses Electron-owned Table Explorer provider for engine-specific execution
  //
  // NOTE: targets `effectiveSchema`/`effectiveTable` so cell edits on a
  // drilled (non-root) TableNode hit THAT node's table, not the tab's
  // original root table.
  const handleExecuteQuickEdit = useCallback(
    async (change: PendingUpdate) => {
      if (!connectionId || !tabContent?.database || !effectiveTable) {
        return { success: false, error: 'Missing connection or table context' };
      }

      // Capture args before the call so the retry closure can replay them.
      lastQuickEditArgsRef.current = change;

      const executionId = createExecutionId(tabId);
      const result = await applyTableExplorerChanges({
        connectionId,
        executionId,
        table: {
          database: tabContent.database,
          schema: effectiveSchema,
          tableName: effectiveTable,
        },
        changes: [change],
      });

      if (result.success) {
        // Refresh data after successful update
        refetch();
        // The single funnel for EVERY single-cell commit in this tab: the
        // grid's quick edit, the row-details panel's pencil and the
        // single-row view's inline editors all reach the DB through here.
        refreshLookupCachesForEditedTable({
          schema: effectiveSchema,
          table: effectiveTable,
        });
        return result;
      }

      // Queue retry when the connection was lost so the banner can offer
      // "Connect and save edits" and replay the exact same change.
      const quickEditFriendlyMsg = checkAndQueueConnectionLostRetry(result.error, {
        tabId,
        connectionId,
        label: 'save edits',
        action: () => {
          const lastChange = lastQuickEditArgsRef.current;
          if (lastChange) {
            void handleExecuteQuickEditRef.current(lastChange);
          }
        },
      });
      if (quickEditFriendlyMsg !== null) {
        return { success: false, error: quickEditFriendlyMsg };
      }

      console.error('[TableExplorerTab] Quick edit failed:', result.error);
      return result;
    },
    [connectionId, tabContent?.database, effectiveSchema, effectiveTable, tabId, refetch, refreshLookupCachesForEditedTable]
  );

  // Blueprint-edit Save — merge pending changes into the sidecar rowset and
  // write back the `.data.json` sidecar + regenerated `.data.sql` upsert. Never
  // touches a live database. Re-reads the sidecar first so the authoritative
  // column metadata (isWritable / isIdentity / pgArrayElementType) and base
  // rows come straight from disk, then applies the pending changes on top.
  const handleBlueprintSave = useCallback(
    async (changes: PendingChange[]) => {
      if (!blueprintEdit || !effectiveTable) {
        return { success: false, error: 'Missing blueprint context' };
      }
      const sidecarRes = await readSidecar({
        folderPath: blueprintEdit.folderPath,
        schema: effectiveSchema,
        name: effectiveTable,
      });
      if (!sidecarRes.success) {
        toast.error({ title: 'Save failed', description: sidecarRes.error });
        return { success: false, error: sidecarRes.error };
      }
      const sidecar: DatabaseBlueprintSidecar = sidecarRes.sidecar ?? {
        pk: primaryKeyColumns,
        columns: (tabContent?.columns ?? []).map((c) => ({ name: c.name, type: c.dataType })),
        rows: [],
      };
      const columnNames = sidecar.columns.map((c) => c.name);
      const pk = sidecar.pk.length > 0 ? [...sidecar.pk] : primaryKeyColumns;
      const baseRows = mapSidecarRowsToObjects(sidecar);
      const mergedRows = applyPendingChangesToRows(baseRows, pk, changes);
      const positional = rowObjectsToPositional(mergedRows, columnNames);

      const result = await applyBlueprintTableEdits({
        folderPath: blueprintEdit.folderPath,
        schema: effectiveSchema,
        name: effectiveTable,
        engine: blueprintEdit.engine,
        pk,
        columns: sidecar.columns.map((c) => ({
          name: c.name,
          type: c.type,
          ...(c.isIdentity ? { isIdentity: true } : {}),
          ...(c.pgArrayElementType ? { pgArrayElementType: c.pgArrayElementType } : {}),
          ...(c.isWritable === false ? { isWritable: false } : {}),
        })),
        rows: positional,
      });

      if (result.success) {
        discardAllChanges();
        // Re-read the freshly-written sidecar into the grid (no live refetch).
        setBlueprintReloadToken((t) => t + 1);
        toast.success({
          title: 'Saved to blueprint',
          description: `Wrote ${result.rows} row${result.rows !== 1 ? 's' : ''} to the blueprint files.`,
        });
        if (exitAfterSave) {
          setExitAfterSave(false);
          writeEditState({ isEditMode: false });
        }
        return { success: true };
      }
      toast.error({ title: 'Save failed', description: result.error });
      return { success: false, error: result.error };
    },
    [blueprintEdit, effectiveSchema, effectiveTable, primaryKeyColumns, tabContent?.columns, discardAllChanges, exitAfterSave, writeEditState],
  );

  // Execute batch save (for full edit mode)
  // Uses Electron-owned Table Explorer provider for engine-specific execution
  //
  // NOTE: targets `effectiveSchema`/`effectiveTable`, and clears the active
  // per-node owner's edit state on success (not blindly the tab-level state).
  const handleExecuteSave = useCallback(
    async (changes: PendingChange[]) => {
      // Blueprint-edit mode writes to the blueprint files, never the live DB.
      if (isBlueprintEdit) {
        return handleBlueprintSave(changes);
      }
      if (!connectionId || !tabContent?.database || !effectiveTable) {
        return { success: false, error: 'Missing connection or table context' };
      }

      // Capture args before the call so the retry closure can replay them.
      lastSaveChangesArgsRef.current = changes;

      const executionId = createExecutionId(tabId);
      const result = await applyTableExplorerChanges({
        connectionId,
        executionId,
        table: {
          database: tabContent.database,
          schema: effectiveSchema,
          tableName: effectiveTable,
        },
        changes,
      });

      if (result.success) {
        // Clear pending changes and refresh data
        discardAllChanges();
        refetch();
        // Batch edit mode: inserts, updates and deletes all land here.
        refreshLookupCachesForEditedTable({
          schema: effectiveSchema,
          table: effectiveTable,
        });

        // Exit edit mode if triggered from exit dialog
        if (exitAfterSave) {
          setExitAfterSave(false);
          writeEditState({ isEditMode: false });
        }
        return result;
      }

      // Queue retry when the connection was lost so the banner can offer
      // "Connect and save edits" and replay the exact same changes.
      const saveFriendlyMsg = checkAndQueueConnectionLostRetry(result.error, {
        tabId,
        connectionId,
        label: 'save edits',
        action: () => {
          const lastChanges = lastSaveChangesArgsRef.current;
          if (lastChanges.length > 0) {
            void handleExecuteSaveRef.current(lastChanges);
          }
        },
      });
      if (saveFriendlyMsg !== null) {
        return { success: false, error: saveFriendlyMsg };
      }

      console.error('[TableExplorerTab] Save changes failed:', result.error);
      return result;
    },
    [isBlueprintEdit, handleBlueprintSave, connectionId, tabContent?.database, effectiveSchema, effectiveTable, tabId, discardAllChanges, refetch, exitAfterSave, writeEditState, refreshLookupCachesForEditedTable]
  );

  // Keep stable retry refs pointing at the latest handler — allows retry closures
  // to call the current version without circular useCallback dependencies.
  handlePreviewQueryRef.current = handlePreviewQuery;
  handleExecuteSaveRef.current = handleExecuteSave;
  handleExecuteQuickEditRef.current = handleExecuteQuickEdit;

  // MCP command handlers for AI agent control
  const mcpHandlers = useMemo(
    () => ({
      onRefresh: () => {
        refetch();
      },
      onShowChart: (show: boolean) => {
        if (show) {
          if (chartConfig) {
            setIsChartViewOpen(true);
          } else {
            setIsChartBuilderOpen(true);
          }
        } else {
          setIsChartViewOpen(false);
          setIsChartBuilderOpen(false);
        }
      },
      onSetChartConfig: (config: ChartConfig) => {
        setChartConfig(config);
        setIsChartBuilderOpen(false);
        setIsChartViewOpen(true);
      },
      onSelectRow: (rowIndex: number) => {
        setSelectedRowIndex(rowIndex);
      },
      onShowRowDetails: (show: boolean, layout: 'bottom' | 'side', rowIndex?: number) => {
        if (show) {
          setRowDetailsLayout(layout);
          const targetIndex = rowIndex ?? selectedRowIndex ?? 0;
          if (tabContent?.rows[targetIndex]) {
            setRowDetailsRowIndex(targetIndex);
            setSelectedRowIndex(targetIndex);
          }
        } else {
          setRowDetailsRowIndex(null);
        }
      },
    }),
    [refetch, chartConfig, selectedRowIndex, tabContent?.rows]
  );

  // Subscribe to MCP commands for this tab
  useTableExplorerMcpCommands(tabId, mcpHandlers);

  // Type guard - we only render this component for table-explorer tabs
  if (!isValidTab || !tabContent) {
    return null;
  }

  // Show banner only if disconnected and not dismissed
  const showDisconnectedBanner = isDisconnected && !bannerDismissed;

  // Show TableSelector if no table is selected
  const showTableSelector = !tabContent.tableName;

  // If no table selected, show the table selector
  if (showTableSelector) {
    return (
      <div className={`h-full flex flex-col overflow-hidden ${className}`}>
        {/* Disconnected Connection Banner */}
        {showDisconnectedBanner && (
          <DisconnectedConnectionBanner
            connectionName={connectionInfo?.connectionName || 'Unknown connection'}
            onReconnect={handleReconnect}
            onDismiss={() => setBannerDismissed(true)}
          />
        )}

        <TableSelector
          connectionId={connectionId || ''}
          database={tabContent.database}
          engineId={engineId}
          onSelectTable={handleTableSelect}
          onDatabaseChange={handleDatabaseChange}
          className="flex-1"
          explorerSelectedTable={explorerSelectedTable}
        />
      </div>
    );
  }

  return (
    <div className={`h-full flex flex-col overflow-hidden ${className}`}>
      {/* Blueprint-edit banner — these rows come from a Database Blueprint's
          Data/ files, NOT a live table. Saving writes back to those files. */}
      {isBlueprintEdit && (
        <div
          data-testid="table-explorer-blueprint-edit-banner"
          className="flex items-center gap-2 px-3 py-1.5 border-b border-blue-300 bg-blue-50 text-blue-800 text-xs dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-200"
        >
          <FileEdit className="h-3.5 w-3.5 shrink-0" />
          <span>
            <strong>Blueprint edit</strong> — changes save to the blueprint files
            (<code className="text-[10.5px]">Data/</code> sidecar +{' '}
            <code className="text-[10.5px]">.data.sql</code>), not the database.
          </span>
        </div>
      )}

      {/* Disconnected Connection Banner */}
      {showDisconnectedBanner && (
        <DisconnectedConnectionBanner
          connectionName={connectionInfo?.connectionName || 'Unknown connection'}
          onReconnect={handleReconnect}
          onDismiss={() => setBannerDismissed(true)}
        />
      )}

      {/* Slice 6 — Connection Diagnostics: per-tab failure banner */}
      {connectionId && tabContent.connectionFailure && !tabContent.connectionFailure.dismissedAt && (() => {
        const failure = tabContent.connectionFailure;
        return (
          <ConnectionTabFailureBanner
            connectionId={connectionId}
            database={failure.scopeDatabase ?? tabContent.database}
            sqlErrorCode={failure.sqlErrorCode}
            onDismiss={() =>
              updateTabContent(tabId, {
                connectionFailure: { ...failure, dismissedAt: Date.now() },
              })
            }
          />
        );
      })()}

      {/* FK Breadcrumb chip-row (Slice 3) — only visible when the navigation
          tree has depth > 1 (the first FK hop reveals it). */}
      {navTree.tree && (
        <FkBreadcrumb
          tabId={tabId}
          tree={navTree.tree}
          onActivate={handleChipActivate}
          onDismiss={handleChipDismiss}
          onSetJoinTableHint={handleSetJoinTableHint}
        />
      )}

      {/* Toolbar — hidden when the record details (single-row) view is active.
          Add filter / Add sort / column selector / etc. are grid-only actions;
          they don't apply when looking at a single row. */}
      {!(isCrossTableActive && activeRowNode) && !singleRowMatch && (
      <TableExplorerToolbar
        tabId={tabId}
        isRowContext={!isTableContext}
        tableName={effectiveTable}
        schema={effectiveSchema}
        isLoading={isLoading}
        isFetching={isFetching}
        onRefresh={handleRefresh}
        onPreviewQuery={handlePreviewQuery}
        onPinToDashboard={connectionId && dashboardsBetaEnabled ? () => void handlePinToDashboard() : undefined}
        supportsQueryPreview={supportsQueryPreview}
        filterCount={effectiveFilters.length}
        sortCount={effectiveSorting.length}
        columns={effectiveMetadataColumns}
        selectedColumns={tabContent.selectedColumns}
        onSelectedColumnsChange={handleSelectedColumnsChange}
        onAddFilter={handleAddFilter}
        onAddSort={handleAddSort}
        addFilterBlocked={filterWizardHasDraft}
        onAddFilterBlocked={handleAddFilterBlocked}
        filteredColumns={filteredColumns}
        sortedColumns={sortedColumns}
        // Edit mode props (per-node owner; root or drilled TableNode)
        isEditMode={effectiveIsEditMode}
        onToggleEditMode={handleToggleEditMode}
        pendingChangeCount={changeCount}
        onAddRow={handleAddRow}
        onSaveChanges={handleOpenSaveChanges}
        onDiscardChanges={handleDiscardChanges}
        hasPrimaryKey={hasPrimaryKey}
        editDisabledReason={
          !engineSupportsEditing
            ? "Azure Data Explorer is append-only — editing isn't available."
            : isWindowedMode
              ? 'Editing unavailable — page too large to load fully. Reduce the page size to enable editing.'
              : undefined
        }
        editDisabledKey={!engineSupportsEditing ? 'read-only-engine' : isWindowedMode ? 'windowed' : undefined}
        onExport={handleExport}
        canExport={tabContent.rows.length > 0}
        isExporting={isExporting}
        onChart={handleOpenChart}
        canChart={tabContent.rows.length > 0}
        onToggleRowSidePanel={handleToggleRowSidePanel}
        isRowSidePanelOpen={rowDetailsRowIndex !== null && rowDetailsLayout === 'side'}
        canOpenRowSidePanel={tabContent.rows.length > 0 && !effectiveIsEditMode}
        onToggleMapPane={handleToggleMapPane}
        isMapPaneOpen={isMapPaneOpen}
        hasSpatialColumn={hasSpatialColumn}
        onCopy={handleCopy}
        canCopy={tabContent.rows.length > 0}
        isCopied={isCopied}
        showColumnProfiling={showColumnProfiling}
        onToggleColumnProfiling={handleToggleColumnProfiling}
        canColumnProfiling={tabContent.rows.length > 0}
        blueprintMode={isBlueprintEdit}
      />
      )}

      {/* Filter Wizard — visible whenever there are editable filters, a
          pending draft, OR locked filters inherited from a non-root
          breadcrumb node. Locked filters render as immutable chips with a
          lock icon. Editable user filters render after them. */}
      {!(isCrossTableActive && activeRowNode) && !singleRowMatch &&
        (effectiveUserFilters.length > 0 ||
        pendingFilterColumn ||
        pendingDraftFilter ||
        effectiveLockedFilters.length > 0) && (
        <FilterWizard
          filters={effectiveUserFilters as FilterGroup[]}
          lockedFilters={effectiveLockedFilters}
          columns={effectiveMetadataColumns}
          onFiltersChange={handleFiltersChange}
          initialDraftColumn={pendingFilterColumn}
          initialDraftFilter={pendingDraftFilter}
          onDraftHandled={handleClearPendingFilter}
          connectionId={connectionId}
          database={tabContent.database}
          tableSchema={effectiveSchema}
          tableName={effectiveTable}
          engine={engineId}
          onDraftStateChange={setFilterWizardHasDraft}
          pulseRequestToken={filterPulseRequest}
          sampleRows={displayRows}
        />
      )}

      {/* Task 32 — spatial index coach chip (TE cheap trigger). Mounted right under the filter
          chips it's reacting to; see the `showSpatialIndexHint` computation above. */}
      {showSpatialIndexHint && activeSpatialFilter && connectionId && (
        <div className="px-3 pt-1" data-testid="spatial-index-hint-container">
          <SpatialIndexHint
            engine={engineId}
            schema={effectiveSchema}
            table={effectiveTable}
            column={activeSpatialFilter.column}
            reason={`Loaded ${tabContent.rows.length.toLocaleString()} rows and hit the page size on "${effectiveTable}" — a spatial index on "${activeSpatialFilter.column}" could speed up this filter.`}
            uncertain={spatialIndexPresence === null}
            bbox={activeSpatialFilter.condition.bbox}
          />
        </div>
      )}

      {/* Sort Wizard - show when there are sorts OR pending draft */}
      {/* Use metadataColumns so all columns are available even when some are hidden */}
      {!(isCrossTableActive && activeRowNode) && !singleRowMatch &&
        (effectiveSorting.length > 0 || pendingSortColumn) && (
        <SortWizard
          sorting={effectiveSorting as SortDefinition[]}
          columns={effectiveMetadataColumns}
          onSortingChange={handleSortingChange}
          initialDraftColumn={pendingSortColumn}
          onDraftHandled={handleClearPendingSort}
        />
      )}

      {/* Windowed-mode banner — shown when the page exceeded the in-memory threshold. */}
      {isWindowedMode && <WindowedModeBanner pageSize={pageSize} />}

      {/* FK Breadcrumb (Slice 3) — Single-Row Details View. The view-mode is
          derived from the active navigation-tree node: row-node → SRV,
          table-node → Grid. Activates the first time the user pushes a
          row-node onto the tree (Slice 1 EP2, plus all new EPs in Slice 3).
          Concern 1 (post-Slice 7) adds a cross-table render branch: when the
          active row-node points at a DIFFERENT `{schema,table}` than the
          underlying grid, fetch the row via `useSingleRowData`.

          Layout (snappy-chip work): the three branches are absolutely
          positioned overlays inside a shared `relative` viewport. The grid
          sits at the bottom of the stack at constant full size; single-row
          views layer on top with z-10. Toggling a single-row view never
          changes the grid container's dimensions, so the grid's
          ResizeObserver / virtualizer never re-measure on navigation —
          re-showing the grid is a true zero-cost reveal, not a re-layout. */}
      <div className="flex-1 min-h-0 relative overflow-hidden">
      {isCrossTableActive && activeRowNode && (
        // z-40 (not z-10) so this overlay stacks above the underlying
        // TableExplorerGrid's sticky <th> elements (zIndex: 25 regular,
        // 30 pinned). With z-10 the sticky headers poked THROUGH the
        // overlay and visually covered the back-button icon area.
        <div className="absolute inset-0 z-40 bg-background overflow-hidden">
          <CrossTableSingleRowView
            connectionId={connectionId || ''}
            database={tabContent.database}
            engine={engineId}
            nodeRef={activeRowNode}
            row={crossTableRowQuery.row}
            columns={crossTableRowQuery.columns}
            inboundForeignKeys={crossTableInboundFks}
            isLoading={crossTableRowQuery.isLoading}
            error={crossTableRowQuery.error}
            onRetry={() => crossTableRowQuery.refetch()}
            onClose={handleCloseSingleRow}
            onFkClick={(args) => {
              const fkInfo = lookupForeignKey(crossTableMetadata.foreignKeyMap, args.column);
              if (!fkInfo) return;
              if (args.modifierKey) {
                import('../../common/services/openDrillTargetAction').then(({ openInNewExplorer }) => {
                  openInNewExplorer({
                    connectionId: connectionId || '',
                    database: tabContent.database,
                    engineId,
                    target: {
                      kind: 'row',
                      schema: fkInfo.referencedSchema || '',
                      table: fkInfo.referencedTable,
                      pk: [{ column: fkInfo.referencedColumn, value: args.value }],
                    },
                  });
                });
                track('feature:fk_nav_open_new_explorer', {
                  source: 'modifier-click-fk',
                  targetKind: 'row',
                  engine: engineId,
                });
                return;
              }
              const treeWasEmpty = navTree.treeWasEmpty;
              navTree.pushRow({
                schema: fkInfo.referencedSchema || '',
                table: fkInfo.referencedTable,
                pk: [{ column: fkInfo.referencedColumn, value: args.value }],
              });
              track('feature:fk_nav_drilldown', {
                engine: engineId,
                source: 'row-details-fk',
                targetKind: 'row',
                direction: 'forward',
                treeWasEmpty,
                viaLooseFk: !!fkInfo.isLoose,
                joinCollapsed: false,
              });
            }}
            onRelatedSectionOpen={({ relation }) => {
              const parentRow = crossTableRowQuery.row;
              if (!parentRow) return;
              const build = buildInboundForeignKeyFilters({
                foreignKey: relation.source,
                parentRow,
                looseRel: undefined,
              }).filters ?? [];
              const treeWasEmpty = navTree.treeWasEmpty;
              navTree.pushTable({
                schema: relation.fromSchema,
                table: relation.fromTable,
                filters: build,
                reverseFkColumn: relation.fromColumn,
              });
              track('feature:fk_nav_drilldown', {
                engine: engineId,
                source: 'related-section-open',
                targetKind: 'table',
                direction: 'reverse',
                treeWasEmpty,
                viaLooseFk: relation.isLoose,
                joinCollapsed: false,
              });
            }}
            onRelatedRowOpen={async ({ relation, row, via, modifierKey }) => {
              if (modifierKey) return;
              const parentRow = crossTableRowQuery.row;
              if (!parentRow) return;
              const tablefilters = buildInboundForeignKeyFilters({
                foreignKey: relation.source,
                parentRow,
                looseRel: undefined,
              }).filters ?? [];
              const treeWasEmpty = navTree.treeWasEmpty;
              const joinCollapsed = await probeJoinCollapse({
                connectionId,
                database: tabContent.database,
                schema: relation.fromSchema,
                table: relation.fromTable,
                hint: pickJoinTableHint(
                  metaInfoForJoinHints?.joinTableHints,
                  relation.fromSchema,
                  relation.fromTable,
                ),
              });
              const pk = buildRowNodePk(row, relation.targetPkColumns);
              if (pk) {
                navTree.pushTableThenRow(
                  {
                    schema: relation.fromSchema,
                    table: relation.fromTable,
                    filters: tablefilters,
                    reverseFkColumn: relation.fromColumn,
                    isJoinCollapsed: joinCollapsed,
                  },
                  {
                    schema: relation.fromSchema,
                    table: relation.fromTable,
                    pk,
                  },
                );
              } else {
                navTree.pushTable({
                  schema: relation.fromSchema,
                  table: relation.fromTable,
                  filters: tablefilters,
                  reverseFkColumn: relation.fromColumn,
                  isJoinCollapsed: joinCollapsed,
                });
              }
              track('feature:fk_nav_drilldown', {
                engine: engineId,
                source: via === 'icon' ? 'related-row-icon' : 'related-row-context',
                targetKind: 'row',
                direction: 'reverse',
                treeWasEmpty,
                viaLooseFk: relation.isLoose,
                joinCollapsed,
              });
            }}
          />
        </div>
      )}
      {!(isCrossTableActive && activeRowNode) && singleRowMatch && (
        // z-40 (not z-10) — see the matching note on the CrossTable branch
        // above. The grid's sticky <th> zIndex (25/30) was poking through
        // z-10 and hiding the SingleRowView header's back-button icon.
        <div className="absolute inset-0 z-40 bg-background overflow-hidden">
          <SingleRowView
            connectionId={connectionId || ''}
            database={tabContent.database}
            engine={engineId}
            nodeRef={{
              kind: 'row',
              schema: singleRowMatch.ref.schema,
              table: singleRowMatch.ref.table,
              pk: singleRowMatch.ref.pk,
            } satisfies SingleRowNodeRef}
            row={singleRowMatch.row}
            columns={tabContent.columns}
            inboundForeignKeys={inboundForeignKeys}
            onClose={handleCloseSingleRow}
            onFkClick={(args) => {
              // EP4 — FK cell click in row-details. We don't yet know the
              // target table from the column alone; resolve via the column's
              // FK metadata if available (`lookupForeignKey`: the exact
              // column name, never a column that only normalizes alike).
              const fkInfo = lookupForeignKey(foreignKeyMap, args.column);
              if (!fkInfo) return;
              if (args.modifierKey) {
                // ⌘-click — open in new explorer.
                import('../../common/services/openDrillTargetAction').then(({ openInNewExplorer }) => {
                  openInNewExplorer({
                    connectionId: connectionId || '',
                    database: tabContent.database,
                    engineId,
                    target: {
                      kind: 'row',
                      schema: fkInfo.referencedSchema || '',
                      table: fkInfo.referencedTable,
                      pk: [{ column: fkInfo.referencedColumn, value: args.value }],
                    },
                  });
                });
                track('feature:fk_nav_open_new_explorer', {
                  source: 'modifier-click-fk',
                  targetKind: 'row',
                  engine: engineId,
                });
                return;
              }
              const treeWasEmpty = navTree.treeWasEmpty;
              navTree.pushRow({
                schema: fkInfo.referencedSchema || '',
                table: fkInfo.referencedTable,
                pk: [{ column: fkInfo.referencedColumn, value: args.value }],
              });
              // Slice 6 — loose FKs ride the same column.isForeignKey flag, but
              // we surface the distinction in analytics so adoption is measurable.
              track('feature:fk_nav_drilldown', {
                engine: engineId,
                source: 'row-details-fk',
                targetKind: 'row',
                direction: 'forward',
                treeWasEmpty,
                viaLooseFk: !!fkInfo.isLoose,
                joinCollapsed: false,
              });
            }}
            onRelatedSectionOpen={({ relation }) => {
              // EP5 — drill into the related table with the inbound filter applied.
              const build = buildInboundForeignKeyFilters({
                foreignKey: relation.source,
                parentRow: singleRowMatch.row,
                looseRel: undefined,
              }).filters ?? [];
              const treeWasEmpty = navTree.treeWasEmpty;
              navTree.pushTable({
                schema: relation.fromSchema,
                table: relation.fromTable,
                filters: build,
                reverseFkColumn: relation.fromColumn,
              });
              track('feature:fk_nav_drilldown', {
                engine: engineId,
                source: 'related-section-open',
                targetKind: 'table',
                direction: 'reverse',
                treeWasEmpty,
                viaLooseFk: relation.isLoose,
                joinCollapsed: false,
              });
            }}
            editable={canQuickEditRows}
            foreignKeyMap={foreignKeyMap}
            onEditCommit={({ column, oldValue, newValue }) => {
              // Build the PK from the currently-viewed row (singleRowMatch.row)
              // using the same shape as the main grid's quick-edit handler.
              const pk: Record<string, unknown> = {};
              for (const col of primaryKeyColumns) {
                pk[col] = singleRowMatch.row[col];
              }
              handleQuickEdit(pk, column, oldValue, newValue);
            }}
            onRelatedRowOpen={async ({ relation, row, via, modifierKey }) => {
              if (modifierKey) return; // handled inside RelatedDataMiniGrid
              // EP6 — push table-node + row-node atomically. `via` selects
              // the analytics source (icon vs context-menu).
              const tablefilters = buildInboundForeignKeyFilters({
                foreignKey: relation.source,
                parentRow: singleRowMatch.row,
                looseRel: undefined,
              }).filters ?? [];
              const treeWasEmpty = navTree.treeWasEmpty;

              // Slice 5 — fire the join-table-collapse heuristic against the
              // referencing-side table (`order_items` in the canonical m2m
              // example) before pushing. We honor the MetaInfo
              // `joinTableHint` override; absence → 4-rule heuristic.
              const joinCollapsed = await probeJoinCollapse({
                connectionId,
                database: tabContent.database,
                schema: relation.fromSchema,
                table: relation.fromTable,
                hint: pickJoinTableHint(
                  metaInfoForJoinHints?.joinTableHints,
                  relation.fromSchema,
                  relation.fromTable,
                ),
              });

              // EP6 — push table-node + row-node atomically (Slice 5).
              // Concern 2: `buildRowNodePk` now consumes the relation's
              // `targetPkColumns` (full composite PK from the referencing
              // table) and falls back to first-column only when the backend
              // didn't enrich the FK metadata. Both this branch and the
              // cross-table branch above share the helper.
              const pk = buildRowNodePk(row, relation.targetPkColumns);
              if (pk) {
                navTree.pushTableThenRow(
                  {
                    schema: relation.fromSchema,
                    table: relation.fromTable,
                    filters: tablefilters,
                    reverseFkColumn: relation.fromColumn,
                    isJoinCollapsed: joinCollapsed,
                  },
                  {
                    schema: relation.fromSchema,
                    table: relation.fromTable,
                    pk,
                  },
                );
              } else {
                navTree.pushTable({
                  schema: relation.fromSchema,
                  table: relation.fromTable,
                  filters: tablefilters,
                  reverseFkColumn: relation.fromColumn,
                  isJoinCollapsed: joinCollapsed,
                });
              }
              track('feature:fk_nav_drilldown', {
                engine: engineId,
                source: via === 'icon' ? 'related-row-icon' : 'related-row-context',
                targetKind: 'row',
                direction: 'reverse',
                treeWasEmpty,
                viaLooseFk: relation.isLoose,
                joinCollapsed,
              });
            }}
          />
        </div>
      )}
      {/* Main Grid Area — always mounted at full viewport size at the bottom
          of the overlay stack. Single-row views (z-10) cover it when active;
          when they unmount, the grid is already laid out and painted, so the
          reveal is instant — no ResizeObserver fire, no virtualizer remeasure. */}
      <div
        ref={rowDetailsSplitRef}
        className="absolute inset-0 overflow-hidden flex flex-col"
      >
        <div className="flex-1 min-h-0 overflow-hidden flex">
          <div className="flex-1 min-w-0 min-h-0 overflow-hidden">
            <TableExplorerGrid
              rows={displayRows}
              columns={displayColumns}
              selectedColumns={tabContent.selectedColumns}
              onColumnReorder={handleColumnReorder}
              // Keep the skeleton up for any in-flight fetch (initial open, refresh,
              // page-size change, filter change) — not just the first cache miss.
              // The grid hides the skeleton again as soon as `rows` has anything to show
              // (populating batches arriving via `populationProgress`).
              isLoading={isLoading || isFetching}
              error={tabContent.error}
              onRefresh={handleRefresh}
              selectedRowIndex={selectedRowIndex}
              onRowSelect={handleSelectRow}
              onAddFilter={handleAddFilter}
              onAddFilterWithValue={handleAddFilterWithValue}
              onAddSort={handleAddSort}
              filteredColumns={filteredColumns}
              sortedColumns={sortedColumns}
              foreignKeyMap={foreignKeyMap}
              connectionId={connectionId}
              database={tabContent.database}
              sourceSchemaForTable={effectiveSchema}
              sourceTableForTable={effectiveTable}
              engine={engineId}
              // Edit mode props — only the root view supports editing. Drilled
              // views (non-root TableNode) are read-only; editing requires the
              // grid to own the table's PK / FK context, which a drilled view
              // doesn't.
              // Per-node edit mode — the grid follows the active TableNode's
              // edit-state (root or drilled). RowNodes don't render this grid.
              isEditMode={effectiveIsEditMode}
              pendingChanges={pendingChanges}
              primaryKeyColumns={primaryKeyColumns}
              insertedRows={insertedRows}
              onCellEdit={handleCellEdit}
              onInsertCellEdit={handleInsertCellEdit}
              onDeleteRow={handleDeleteRow}
              onUndoDelete={handleUndoDelete}
              onRemoveInsert={handleRemoveInsert}
              onQuickEdit={handleQuickEdit}
              canQuickEdit={canQuickEditRows && !effectiveIsEditMode}
              // Row details panel
              expandedRowDetailsIndex={rowDetailsLayout === 'bottom' ? rowDetails?.rowIndex ?? null : null}
              onToggleRowDetails={handleToggleRowDetails}
              // LOB support (Oracle)
              lobColumns={lobColumns}
              onFetchFullCellValue={lobColumns ? handleFetchFullCellValue : undefined}
              zoomLevel={zoomLevel}
              columnProfiles={columnProfiles}
              showColumnProfiling={showColumnProfiling}
              onProfileColumn={
                connectionId && tabContent.tableName
                  ? (columnName) =>
                      openDataProfileTab({
                        connectionId,
                        database: tabContent.database ?? '',
                        schema: tabContent.schema ?? '',
                        table: tabContent.tableName,
                        focusedColumn: columnName,
                        entryPoint: 'table_explorer_grid',
                      })
                  : undefined
              }
              onFindUsages={
                isFindUsagesSupportedEngine(engineId) && connectionId && tabContent.tableName
                  ? (columnName) =>
                      openFindUsagesTab({
                        connectionId,
                        database: tabContent.database ?? '',
                        schema: tabContent.schema ?? '',
                        table: tabContent.tableName,
                        columnName,
                        source: 'table_explorer_grid_header',
                      })
                  : undefined
              }
              onOpenRecordDetails={hasPrimaryKey ? handleOpenRecordDetails : undefined}
              onOpenRowInNewExplorer={
                hasPrimaryKey && connectionId
                  ? ({ row }) => {
                      const pk = primaryKeyColumns.map((col) => ({ column: col, value: row[col] }));
                      // Reuse openInNewExplorer via the table-explorer tab open helper:
                      // build a filter on the PK so the new tab opens a 1-row view.
                      // The helper imports lazily to avoid a circular import.
                      import('../../common/services/openDrillTargetAction').then(({ openInNewExplorer }) => {
                        openInNewExplorer({
                          connectionId,
                          database: tabContent.database,
                          engineId,
                          target: { kind: 'row', schema: tabContent.schema, table: tabContent.tableName, pk },
                        });
                      });
                      track('feature:fk_nav_open_new_explorer', {
                        source: 'row-context-menu',
                        targetKind: 'row',
                        engine: engineId,
                      });
                    }
                  : undefined
              }
              onOpenRowInQueryEditor={
                hasPrimaryKey && connectionId
                  ? ({ row }) => {
                      const pk = primaryKeyColumns.map((col) => ({ column: col, value: row[col] }));
                      import('../../common/services/openDrillTargetAction').then(({ openInQueryEditor }) => {
                        openInQueryEditor({
                          connectionId,
                          database: tabContent.database,
                          engineId,
                          target: { kind: 'row', schema: tabContent.schema, table: tabContent.tableName, pk },
                          connectionName: connectionInfo?.connectionName,
                          serverHost: connectionInfo?.serverHost,
                        });
                      });
                      track('feature:fk_nav_open_query_editor', {
                        source: 'row-context-menu',
                        targetKind: 'row',
                        engine: engineId,
                      });
                    }
                  : undefined
              }
              activeFilterSignature={activeFilterSignature}
            />
          </div>

          {rowDetails && connectionId && !effectiveIsEditMode && rowDetailsLayout === 'side' && (
            <>
              <div
                className="flex-shrink-0 w-2 cursor-col-resize flex items-center justify-center relative bg-transparent before:absolute before:inset-y-0 before:left-1/2 before:-translate-x-1/2 before:w-px before:bg-divider before:transition-colors before:content-[''] hover:before:bg-primary/60 data-[dragging=true]:before:bg-primary/60 outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background"
                onPointerDown={handleRowDetailsSidePointerDown}
                onDoubleClick={rowDetailsSideSplitter.onDoubleClick}
                onKeyDown={rowDetailsSideSplitter.onKeyDown}
                style={{ touchAction: 'none', userSelect: 'none' }}
                data-testid="row-details-side-resize-divider"
                aria-label="Resize row details side panel"
                title="Drag to resize"
                role="separator"
                aria-orientation="vertical"
                aria-valuenow={rowDetailsSideWidthPx}
                aria-valuemin={rowDetailsSideMinWidth}
                aria-valuemax={rowDetailsSideMaxWidth}
                aria-valuetext={`${rowDetailsSideWidthPx} pixels`}
                tabIndex={0}
                data-dragging={rowDetailsSideSplitter.isDragging ? 'true' : undefined}
              >
                <div className="h-8 w-1 bg-border rounded-full" />
              </div>
              <div className="flex-shrink-0 overflow-hidden" style={{ width: rowDetailsSideWidthPx }}>
                <RowDetailsPanel
                  connectionId={connectionId}
                  database={tabContent.database}
                  schema={tabContent.schema}
                  tableName={tabContent.tableName}
                  rowIndex={rowDetails.rowIndex}
                  row={rowDetails.row}
                  columns={tabContent.columns}
                  foreignKeyMap={foreignKeyMap}
                  layout="side"
                  onSwitchLayout={setRowDetailsLayout}
                  relatedData={{
                    supportsInboundForeignKeyExplorer,
                    inboundForeignKeys,
                    isInboundForeignKeysLoading: inboundForeignKeysQuery.isLoading,
                    inboundForeignKeysError: inboundForeignKeysQuery.error,
                    onRetryInboundForeignKeys: () => inboundForeignKeysQuery.refetch(),
                  }}
                  onClose={() => setRowDetailsRowIndex(null)}
                  canQuickEdit={canQuickEditRows}
                  primaryKeyColumns={primaryKeyColumns}
                  onQuickEdit={handleQuickEdit}
                  engineId={engineId}
                  onDrillInboundFk={({ foreignKey, looseRel }) => {
                    // Slice 3 (EP5 parity for inline panel) — push a
                    // filtered table-node onto the nav tree. Mirrors the
                    // RelatedDataSection "Open" path in SingleRowView.
                    const build = buildInboundForeignKeyFilters({
                      foreignKey,
                      parentRow: rowDetails.row,
                      looseRel,
                    });
                    const treeWasEmpty = navTree.treeWasEmpty;
                    navTree.ensureRoot({
                      schema: tabContent.schema,
                      table: tabContent.tableName,
                      filters: [],
                    });
                    navTree.pushTable({
                      schema: foreignKey.referencingSchema,
                      table: foreignKey.referencingTable,
                      filters: build.filters ?? [],
                      reverseFkColumn: foreignKey.referencingColumns?.[0],
                    });
                    setRowDetailsRowIndex(null);
                    track('feature:fk_nav_drilldown', {
                      engine: engineId,
                      source: 'related-section-open',
                      targetKind: 'table',
                      direction: 'reverse',
                      treeWasEmpty,
                      viaLooseFk: !!foreignKey.isLoose,
                      joinCollapsed: false,
                    });
                  }}
                  onOpenRecord={
                    hasPrimaryKey && rowDetails
                      ? () => {
                          const treeWasEmpty = navTree.treeWasEmpty;
                          const pk = primaryKeyColumns.map((col) => ({ column: col, value: rowDetails.row[col] }));
                          navTree.ensureRoot({
                            schema: tabContent.schema,
                            table: tabContent.tableName,
                            filters: [],
                          });
                          navTree.pushRow({ schema: tabContent.schema, table: tabContent.tableName, pk });
                          setRowDetailsRowIndex(null);
                          track('feature:fk_nav_drilldown', {
                            engine: engineId,
                            source: 'inline-row-panel',
                            targetKind: 'row',
                            direction: 'same-row',
                            treeWasEmpty,
                            viaLooseFk: false,
                            joinCollapsed: false,
                          });
                        }
                      : undefined
                  }
                />
              </div>
            </>
          )}

          {/* Spatial map pane (T14) — rightmost grid|map split. Full-height flex
              child (definite size) so the maplibre canvas fills it, never the
              300px fallback. Two-way selection: feature click → grid row select;
              grid row select → feature highlight (selectedRowIndex prop). */}
          {isMapPaneOpen && hasSpatialColumn && !effectiveIsEditMode && (
            <>
              <div
                className="flex-shrink-0 w-2 cursor-col-resize flex items-center justify-center relative bg-transparent before:absolute before:inset-y-0 before:left-1/2 before:-translate-x-1/2 before:w-px before:bg-divider before:transition-colors before:content-[''] hover:before:bg-primary/60 data-[dragging=true]:before:bg-primary/60 outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background"
                onPointerDown={handleMapPanePointerDown}
                onDoubleClick={mapPaneSplitter.onDoubleClick}
                onKeyDown={mapPaneSplitter.onKeyDown}
                style={{ touchAction: 'none', userSelect: 'none' }}
                data-testid="table-explorer-map-resize-divider"
                aria-label="Resize map pane"
                title="Drag to resize"
                role="separator"
                aria-orientation="vertical"
                aria-valuenow={mapPaneWidthPx}
                aria-valuemin={mapPaneMinWidth}
                aria-valuemax={mapPaneMaxWidth}
                aria-valuetext={`${mapPaneWidthPx} pixels`}
                tabIndex={0}
                data-dragging={mapPaneSplitter.isDragging ? 'true' : undefined}
              >
                <div className="h-8 w-1 bg-border rounded-full" />
              </div>
              <div
                className="relative flex-shrink-0 h-full overflow-hidden"
                style={{ width: mapPaneWidthPx }}
                data-testid="table-explorer-map-pane"
              >
                {/* The spatial filter buttons ("in view" + "draw area") ride in the map toolbar's
                    Filter slot (see SpatialResultsMapControls) — they used to float over the map
                    and overlapped the color-by selector. */}
                <SpatialResultsMapPanel
                  resultSet={spatialResultSet}
                  engine={engineId}
                  connectionId={connectionId}
                  database={tabContent.database}
                  selectedRowIndex={selectedRowIndex}
                  onRowSelect={setSelectedRowIndex}
                  onViewportChange={setSpatialViewport}
                  onMapClick={handleSpatialMapClick}
                  onSridsChange={setSpatialColumnSrids}
                  draw={spatialDraw.draw}
                  surface="table-explorer"
                  emptyRowsLabel="No rows in the current window"
                  filterControls={
                    spatialFilterColumn ? (
                      <>
                        <button
                          type="button"
                          onClick={handleFilterByMapView}
                          disabled={!spatialViewport}
                          title={
                            spatialViewport
                              ? 'Add a filter for rows whose geometry intersects the current map view'
                              : 'Pan or zoom the map first to set a view'
                          }
                          aria-label="Filter by current map view"
                          data-testid="spatial-filter-by-map-view"
                          className="inline-flex items-center gap-1 rounded border border-input bg-background px-1.5 py-0.5 text-xs hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Frame className="h-3.5 w-3.5" />
                          In view
                        </button>
                        {spatialDraw.canDraw && (
                          <button
                            type="button"
                            onClick={spatialDraw.toggleDraw}
                            aria-pressed={spatialDraw.drawActive}
                            title={
                              spatialDraw.drawActive
                                ? 'Click points on the map to outline an area; double-click or Enter to finish, Esc to cancel'
                                : 'Draw an area on the map to filter rows whose geometry falls inside it'
                            }
                            aria-label="Draw an area to filter"
                            data-testid="spatial-filter-draw-area"
                            className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-xs ${
                              spatialDraw.drawActive
                                ? 'border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-200'
                                : 'border-input bg-background hover:bg-accent'
                            }`}
                          >
                            <Hexagon className="h-3.5 w-3.5" />
                            {spatialDraw.drawActive ? 'Drawing… (Esc)' : 'Draw area'}
                          </button>
                        )}
                      </>
                    ) : undefined
                  }
                />
              </div>
            </>
          )}
        </div>

        {rowDetails && connectionId && !effectiveIsEditMode && rowDetailsLayout === 'bottom' && (
          <>
            <div
              className="flex-shrink-0 h-1.5 cursor-row-resize flex items-center justify-center relative bg-transparent before:absolute before:inset-x-0 before:top-1/2 before:-translate-y-1/2 before:h-px before:bg-divider before:transition-colors before:content-[''] hover:before:bg-primary/60 data-[dragging=true]:before:bg-primary/60 outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background"
              onPointerDown={handleRowDetailsPointerDown}
              onDoubleClick={rowDetailsBottomSplitter.onDoubleClick}
              onKeyDown={rowDetailsBottomSplitter.onKeyDown}
              style={{
                touchAction: 'none',
                userSelect: 'none',
              }}
              data-testid="row-details-resize-divider"
              aria-label="Resize row details panel"
              title="Drag to resize"
              role="separator"
              aria-orientation="horizontal"
              aria-valuenow={rowDetailsHeightPx}
              aria-valuemin={rowDetailsMinHeight}
              aria-valuemax={
                rowDetailsSplitRef.current
                  ? Math.round(
                      Math.max(
                        rowDetailsMinHeight,
                        rowDetailsSplitRef.current.getBoundingClientRect().height - gridMinHeight - rowDetailsDividerHeight
                      )
                    )
                  : undefined
              }
              aria-valuetext={`${rowDetailsHeightPx} pixels`}
              tabIndex={0}
              data-dragging={rowDetailsBottomSplitter.isDragging ? 'true' : undefined}
            >
              <div className="w-8 h-1 bg-border rounded-full" />
            </div>
            <div className="flex-shrink-0 overflow-hidden" style={{ height: rowDetailsHeightPx }}>
              <RowDetailsPanel
                connectionId={connectionId}
                database={tabContent.database}
                schema={tabContent.schema}
                tableName={tabContent.tableName}
                rowIndex={rowDetails.rowIndex}
                row={rowDetails.row}
                columns={tabContent.columns}
                foreignKeyMap={foreignKeyMap}
                layout="bottom"
                onSwitchLayout={setRowDetailsLayout}
                relatedData={{
                  supportsInboundForeignKeyExplorer,
                  inboundForeignKeys,
                  isInboundForeignKeysLoading: inboundForeignKeysQuery.isLoading,
                  inboundForeignKeysError: inboundForeignKeysQuery.error,
                  onRetryInboundForeignKeys: () => inboundForeignKeysQuery.refetch(),
                }}
                onClose={() => setRowDetailsRowIndex(null)}
                canQuickEdit={canQuickEditRows}
                primaryKeyColumns={primaryKeyColumns}
                onQuickEdit={handleQuickEdit}
                onOpenRecord={
                  hasPrimaryKey && rowDetails
                    ? () => {
                        const treeWasEmpty = navTree.treeWasEmpty;
                        const pk = primaryKeyColumns.map((col) => ({ column: col, value: rowDetails.row[col] }));
                        navTree.ensureRoot({
                          schema: tabContent.schema,
                          table: tabContent.tableName,
                          filters: [],
                        });
                        navTree.pushRow({ schema: tabContent.schema, table: tabContent.tableName, pk });
                        setRowDetailsRowIndex(null);
                        track('feature:fk_nav_drilldown', {
                          engine: engineId,
                          source: 'inline-row-panel',
                          targetKind: 'row',
                          direction: 'same-row',
                          treeWasEmpty,
                          viaLooseFk: false,
                          joinCollapsed: false,
                        });
                      }
                    : undefined
                }
                engineId={engineId}
                onDrillInboundFk={({ foreignKey, looseRel }) => {
                  const build = buildInboundForeignKeyFilters({
                    foreignKey,
                    parentRow: rowDetails.row,
                    looseRel,
                  });
                  const treeWasEmpty = navTree.treeWasEmpty;
                  navTree.ensureRoot({
                    schema: tabContent.schema,
                    table: tabContent.tableName,
                    filters: [],
                  });
                  navTree.pushTable({
                    schema: foreignKey.referencingSchema,
                    table: foreignKey.referencingTable,
                    filters: build.filters ?? [],
                    reverseFkColumn: foreignKey.referencingColumns?.[0],
                  });
                  setRowDetailsRowIndex(null);
                  track('feature:fk_nav_drilldown', {
                    engine: engineId,
                    source: 'related-section-open',
                    targetKind: 'table',
                    direction: 'reverse',
                    treeWasEmpty,
                    viaLooseFk: !!foreignKey.isLoose,
                    joinCollapsed: false,
                  });
                }}
              />
            </div>
          </>
        )}
      </div>
      </div>

      {/* Pagination — only meaningful for the table grid, hide in single-row view */}
      {showPaginationStrip && (
        <TableExplorerPagination
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          totalRowCount={totalRowCount}
          totalUnknown={paginationModel.totalUnknown}
          totalIsEstimate={paginationModel.totalIsEstimate}
          hasNextPage={paginationModel.hasNextPage}
          rangeStart={paginationModel.rangeStart}
          rangeEnd={paginationModel.rangeEnd}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          isLoading={isLoading || isFetching}
          isFetching={isFetching}
          populationProgress={populationProgress}
          onCancelFetch={cancelFetch}
          isCancelling={isCancelling}
          zoomLevel={zoomLevel}
          onZoomChange={setZoomLevel}
        />
      )}

      {/* Status Bar */}
      <TableExplorerStatusBar
        tableName={effectiveTable}
        schema={effectiveSchema}
        rowCount={showPaginationStrip ? undefined : tabContent.rows.length}
        totalRowCount={showPaginationStrip ? undefined : tabContent.totalRowCount ?? tabContent.estimatedRowCount}
        totalIsEstimate={paginationModel.totalIsEstimate}
        connectionName={connectionInfo?.connectionName}
        connectionColor={savedConnections.find((c) => c.id === connectionInfo?.connectionId)?.color}
        database={tabContent.database}
        engine={engineId}
        filterCount={effectiveFilters.length}
        sortCount={effectiveSorting.length}
      />

      {/* Query Preview Dialog */}
      <QueryPreviewDialog
        open={isQueryPreviewOpen}
        onOpenChange={setIsQueryPreviewOpen}
        query={previewQuery}
        engineId={engineId}
        onOpenInQueryEditor={handleOpenInQueryEditor}
        tableName={tabContent.tableName}
      />

      {/* Save Changes Dialog (Full Edit Mode) — targets the active per-node
          owner's table (effective schema/table), so saving from a drilled
          breadcrumb node writes to THAT node's table. */}
      <SaveChangesDialog
        open={isSaveChangesOpen}
        onOpenChange={(open) => {
          setIsSaveChangesOpen(open);
          // Reset exit flag if dialog is cancelled
          if (!open) setExitAfterSave(false);
        }}
        connectionId={connectionId || ''}
        engineId={engineId}
        database={tabContent.database}
        changes={pendingChanges}
        schema={effectiveSchema}
        tableName={effectiveTable}
        supportsQueryPreview={supportsQueryPreview}
        onOpenInQueryEditor={handleOpenInQueryEditor}
        onApply={handleExecuteSave}
      />

      {/* Quick Edit Dialog (Context Menu Single Cell Edit) — targets the
          effective table so quick edits on drilled views update the right
          row in the right table. */}
      {quickEditData && (
        <QuickEditDialog
          open={isQuickEditOpen}
          onOpenChange={handleQuickEditOpenChange}
          supportsQueryPreview={supportsQueryPreview}
          connectionId={connectionId || ''}
          engineId={engineId}
          database={tabContent.database}
          primaryKey={quickEditData.primaryKey}
          field={quickEditData.field}
          oldValue={quickEditData.oldValue}
          newValue={quickEditData.newValue}
          schema={effectiveSchema}
          tableName={effectiveTable}
          onApply={handleExecuteQuickEdit}
          onOpenInQueryEditor={handleOpenInQueryEditor}
        />
      )}

      {/* Exit Edit Mode Dialog (Unsaved Changes Warning) */}
      <ExitEditModeDialog
        open={isExitEditModeOpen}
        onOpenChange={setIsExitEditModeOpen}
        pendingChangeCount={changeCount}
        onSave={handleExitAfterSave}
        onDiscard={handleExitAfterDiscard}
      />

      {/* Chip-Dismiss Confirmation — fires when the user dismisses a chip
          whose subtree contains pending edits. Discard removes the chip +
          subtree (and the edits); Cancel keeps everything. We don't expose
          Save here because the dismissed node may not be the active edit
          owner — the user can save through the normal toolbar first. */}
      <DismissBreadcrumbConfirmDialog
        open={pendingDismissId !== null}
        onOpenChange={(open) => {
          if (!open) handleConfirmDismissCancel();
        }}
        pendingChangeCount={pendingDismissChangeCount}
        onDiscard={handleConfirmDismissDiscard}
      />

      {/* Chart Builder Dialog */}
      <ChartBuilderDialog
        open={isChartBuilderOpen}
        onOpenChange={setIsChartBuilderOpen}
        columns={chartColumns}
        rows={tabContent.rows}
        existingConfig={chartConfig}
        onConfigured={handleChartConfigured}
        spatialColumnIds={chartSpatialColumnIds}
      />

      {/* Chart View Dialog */}
      <Dialog open={isChartViewOpen} onOpenChange={setIsChartViewOpen}>
        <DialogContent className="sm:max-w-[800px] h-[600px] flex flex-col">
          <DialogHeader>
            <DialogTitle>
              {chartConfig?.title || `Chart: ${tabContent.tableName}`}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-hidden">
            {chartConfig && hasChartData ? (
              <ChartRendererHost
                data={chartData}
                config={resolvedChartConfig ?? chartConfig}
                height={480}
              />
            ) : (
              <div className="flex items-center justify-center h-full text-muted-foreground">
                No chart data available
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <button
              type="button"
              className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-9 px-4 py-2"
              onClick={() => {
                setIsChartViewOpen(false);
                setIsChartBuilderOpen(true);
              }}
            >
              Configure
            </button>
            <button
              type="button"
              className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-9 px-4 py-2"
              onClick={() => {
                setChartConfig(undefined);
                setIsChartViewOpen(false);
              }}
            >
              Reset
            </button>
            <button
              type="button"
              className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-9 px-4 py-2"
              onClick={() => setIsChartViewOpen(false)}
            >
              Close
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Export Limit Dialog (Personal Mode) */}
      <PersonalModeExportLimitMessage
        open={showExportLimitDialog}
        onOpenChange={setShowExportLimitDialog}
        rowCount={exportLimitRowCount}
        onUpgrade={() => {
          setShowExportLimitDialog(false);
          setShowUpgradeDialog(true);
        }}
      />

      {/* Upgrade Dialog */}
      <UpgradeDialog
        open={showUpgradeDialog}
        onOpenChange={setShowUpgradeDialog}
      />

    </div>
  );
}
